// src/editor/GlobalEditor.jsx
'use client';

import { Disclosure, Listbox, Transition } from '@headlessui/react';
import {
    AdjustmentsHorizontalIcon,
    ArrowUturnLeftIcon,
    CheckIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CloudArrowUpIcon,
    Cog6ToothIcon as Cog6ToothIconSolid,
    ComputerDesktopIcon,
    DevicePhoneMobileIcon,
    DeviceTabletIcon,
    DocumentTextIcon,
    EyeIcon,
    EyeSlashIcon,
    ExclamationTriangleIcon,
    FolderIcon,
    HomeIcon,
    InformationCircleIcon,
    LinkIcon,
    PlusIcon,
} from '@heroicons/react/20/solid';
import {
    Cog6ToothIcon as Cog6ToothIconOutline,
    DocumentPlusIcon,
    DocumentDuplicateIcon,
    FolderPlusIcon,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { usePathname, useRouter, useSearchParams } from 'next/navigation.js';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { AddComponent } from './AddComponent.jsx';
import ComponentEditor from './ComponentEditor.jsx';
import { ConfirmationModal } from './ConfirmationModal.jsx';
import { EditorDrawer } from './EditorDrawer.jsx';
import { SiteSettingsEditor } from './SiteSettingsEditor.jsx';
import { UpdateComponentsButton } from './UpdateComponentsButton.jsx';
import { UpdateEditor } from './UpdateEditor.jsx';
import { useEditor } from './context/EditorContext/EditorContext.jsx';
import {
    NotificationPanel,
    useNotifications,
} from './context/NotificationContext/NotificationContext.jsx';
import { finalizePagePath, sanitizePagePathInput } from './helpers/pagePath';
import { Publish } from './site-settings/Publish.jsx';
import { API } from './utils/api';

/* ───────────────────── Helpers ───────────────────── */

const NICEJOB_LOGO = '/editor-assets/nicejob-primary-white.svg?v=logo-1';
const PAGE_MENU_PENDING_ACTION_KEY = 'editor:page-menu-pending-action';
const PAGE_MENU_CACHED_PAGES_KEY_PREFIX = 'editor:page-menu:pages';
const PAGE_MENU_CACHED_REDIRECTS_KEY_PREFIX = 'editor:redirects';
const PAGE_MENU_ORDER_GLOBAL_KEY = 'editor_site_pages_order';
const DUPLICATE_PAGE_TYPE_FALLBACK = 'servicelocation';

const DUPLICATE_PAGE_TYPE_WHITELIST = new Set([
    'servicelocation',
    'faq',
    'work',
    'reviews',
    'privacy',
    'terms-and-conditions',
    'about-us',
]);
const HIDDEN_SITE_PAGES_MENU_ROUTES = new Set(['servicelocation']);

const NEW_PAGE_TEMPLATE_OPTIONS = [
    {
        value: 'servicelocation',
        label: 'Landing Page',
    },
    {
        value: 'faq',
        label: 'FAQ',
    },
    {
        value: 'work',
        label: 'Work',
    },
    {
        value: 'reviews',
        label: 'Reviews',
    },
    {
        value: 'privacy',
        label: 'Privacy',
    },
    {
        value: 'terms-and-conditions',
        label: 'Terms & Conditions',
    },
    {
        value: 'about-us',
        label: 'About Us',
    },
];

function normalizePageName(value) {
    const raw = String(value || '')
        .trim()
        .replace(/^\/+|\/+$/g, '');
    return raw ? raw : 'index';
}

function normalizeStoredPageOrder(value) {
    if (!Array.isArray(value)) return [];
    return value
        .map((page) => normalizePageName(page))
        .filter(isVisibleSitePagesMenuPage);
}

function getPageOrderSignature(pages) {
    if (!Array.isArray(pages)) return '';
    return pages
        .map((page) => normalizePageName(page))
        .filter(isVisibleSitePagesMenuPage)
        .join('|');
}

function getCachedPagesStorageKey(companyId) {
    return `${PAGE_MENU_CACHED_PAGES_KEY_PREFIX}:${companyId}`;
}

function getCachedRedirectsStorageKey(companyId) {
    return `${PAGE_MENU_CACHED_REDIRECTS_KEY_PREFIX}:${companyId}`;
}

function readCachedPages(companyId) {
    if (typeof window === 'undefined' || !companyId) return [];
    try {
        const raw = window.localStorage.getItem(
            getCachedPagesStorageKey(companyId)
        );
        if (!raw) return [];

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((page) => normalizePageName(page))
            .filter(isVisibleSitePagesMenuPage);
    } catch {
        return [];
    }
}

function writeCachedPages(companyId, pages) {
    if (typeof window === 'undefined' || !companyId) return;
    try {
        const normalized = Array.isArray(pages)
            ? pages
                  .map((page) => normalizePageName(page))
                  .filter(isVisibleSitePagesMenuPage)
            : [];

        window.localStorage.setItem(
            getCachedPagesStorageKey(companyId),
            JSON.stringify([...new Set(normalized)])
        );
    } catch {
        // ignore localStorage write issues
    }
}

function normalizeRedirectsForCache(value) {
    if (!Array.isArray(value)) return [];
    return value
        .filter((rule) => rule && typeof rule === 'object')
        .map((rule) => ({
            source: String(rule.source || '').trim(),
            destination: String(rule.destination || '').trim(),
            permanent: rule.permanent !== false,
        }))
        .filter((rule) => !!rule.source && !!rule.destination);
}

function readCachedRedirects(companyId) {
    if (typeof window === 'undefined' || !companyId) return [];
    try {
        const raw = window.localStorage.getItem(
            getCachedRedirectsStorageKey(companyId)
        );
        if (!raw) return [];
        return normalizeRedirectsForCache(JSON.parse(raw));
    } catch {
        return [];
    }
}

function writeCachedRedirects(companyId, redirects) {
    if (typeof window === 'undefined' || !companyId) return;
    try {
        window.localStorage.setItem(
            getCachedRedirectsStorageKey(companyId),
            JSON.stringify(normalizeRedirectsForCache(redirects))
        );
    } catch {
        // ignore localStorage write issues
    }
}

function isSelectableEditorPage(value) {
    const normalized = normalizePageName(value);
    if (!normalized) return false;
    if (normalized === 'index') return true;

    const blockedSegments = new Set(['api', 'lib', 'editor']);
    const segments = normalized.split('/').filter(Boolean);

    // Exclude technical/internal routes and file-like paths (e.g. llms.txt).
    return segments.every((segment) => {
        const lower = segment.toLowerCase();
        return (
            !blockedSegments.has(lower) &&
            !lower.startsWith('_') &&
            !lower.includes('(') &&
            !lower.includes(')') &&
            !lower.includes('[') &&
            !lower.includes(']') &&
            !lower.includes('.')
        );
    });
}

function isHiddenInSitePagesMenu(value) {
    const normalized = normalizePageName(value);
    return HIDDEN_SITE_PAGES_MENU_ROUTES.has(normalized);
}

function isVisibleSitePagesMenuPage(value) {
    return isSelectableEditorPage(value) && !isHiddenInSitePagesMenu(value);
}

function parsePublishedValue(value) {
    if (value == null) return true;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return Boolean(value);
}

function parseNoIndexValue(value) {
    if (value == null) return false;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return Boolean(value);
}

function getStaticJsonPath(page, fileName) {
    return page === 'index'
        ? `/static/json/index/${fileName}`
        : `/static/json/${page}/${fileName}`;
}

function getDuplicateTemplateType(sourcePage) {
    const topLevel = normalizePageName(sourcePage).split('/')[0];
    if (DUPLICATE_PAGE_TYPE_WHITELIST.has(topLevel)) return topLevel;
    return DUPLICATE_PAGE_TYPE_FALLBACK;
}

async function fetchSiteRoutePages() {
    const normalizeRouteList = (routes) => {
        if (!Array.isArray(routes)) return [];
        return routes
            .map((route) => {
                const raw = String(route ?? '')
                    .trim()
                    .split('?')[0]
                    .split('#')[0];
                if (!raw || raw === '/') return 'index';
                return normalizePageName(raw);
            })
            .filter(Boolean);
    };

    const fetchFromSiteRoutesApi = async () => {
        const response = await fetch('/api/site-routes', {
            cache: 'no-store',
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch site routes: ${response.status}`);
        }

        const payload = await response.json();
        return normalizeRouteList(payload?.pages);
    };

    const extractRoutesFromManifestValue = (manifestValue) => {
        const routes = new Set();

        if (manifestValue instanceof Set) {
            manifestValue.forEach((route) => routes.add(String(route)));
        } else if (Array.isArray(manifestValue?.sortedPages)) {
            manifestValue.sortedPages.forEach((route) =>
                routes.add(String(route))
            );
        } else if (manifestValue && typeof manifestValue === 'object') {
            Object.keys(manifestValue).forEach((route) => routes.add(route));
        }

        return routes;
    };

    const extractRouteLiteralsFromText = (text) => {
        const routes = new Set();
        const routePattern = /['"]((?:\/[a-z0-9-_]+)*\/?)['"]/gi;
        let match = null;

        while ((match = routePattern.exec(text)) !== null) {
            const route = match[1];
            if (!route || route === '/_next' || route.startsWith('/_next/')) {
                continue;
            }
            routes.add(route);
        }

        return routes;
    };

    const fetchFromBuildManifest = async () => {
        if (typeof window === 'undefined') return [];

        const buildId =
            window.__NEXT_DATA__?.buildId ||
            document
                .querySelector('script#__NEXT_DATA__')
                ?.textContent?.match(/"buildId":"([^"]+)"/)?.[1];

        if (!buildId) return [];

        const manifestUrls = [
            `/_next/static/${buildId}/_buildManifest.js`,
            `/_next/static/${buildId}/_ssgManifest.js`,
        ];

        const discoveredRoutes = new Set();

        for (const manifestUrl of manifestUrls) {
            try {
                const response = await fetch(manifestUrl, {
                    cache: 'no-store',
                });
                if (!response.ok) continue;

                const source = await response.text();

                try {
                    const getManifestValue = new Function(
                        'self',
                        'window',
                        'globalThis',
                        `${source}; return self.__BUILD_MANIFEST || self.__SSG_MANIFEST || window.__BUILD_MANIFEST || window.__SSG_MANIFEST || globalThis.__BUILD_MANIFEST || globalThis.__SSG_MANIFEST || null;`
                    );

                    const selfObj = {};
                    const windowObj = {};
                    const globalObj = {};
                    const manifestValue = getManifestValue(
                        selfObj,
                        windowObj,
                        globalObj
                    );

                    extractRoutesFromManifestValue(manifestValue).forEach(
                        (route) => discoveredRoutes.add(route)
                    );
                } catch {
                    // Fall through to regex extraction.
                }

                extractRouteLiteralsFromText(source).forEach((route) =>
                    discoveredRoutes.add(route)
                );
            } catch {
                // Try next manifest source.
            }
        }

        return normalizeRouteList(Array.from(discoveredRoutes));
    };

    try {
        const apiRoutes = await fetchFromSiteRoutesApi();
        if (apiRoutes.length) return apiRoutes;
    } catch {
        // Fallback for environments where app/api routes are unavailable.
    }

    const manifestRoutes = await fetchFromBuildManifest();
    if (manifestRoutes.length) return manifestRoutes;

    throw new Error('Failed to detect site routes');
}

async function fetchEditorApiPages(companyId, { recursive = false } = {}) {
    const payload = await API.get(`${companyId}/pages`, {
        params: recursive ? { recursive: 'true' } : {},
    });

    if (!Array.isArray(payload)) return [];
    return payload
        .map((page) => normalizePageName(page))
        .filter(isVisibleSitePagesMenuPage);
}

function pageNameToPath(value) {
    const normalized = normalizePageName(value);
    return normalized === 'index' ? '/' : `/${normalized}`;
}

function normalizeRoutePath(value) {
    const raw = String(value ?? '').trim();
    if (!raw || raw === 'index' || raw === '/') return '/';
    const cleaned = raw.replace(/^\/+|\/+$/g, '');
    return cleaned ? `/${cleaned}` : '/';
}

function formatPageName(value) {
    const normalized = normalizePageName(value);
    if (normalized === 'index') return 'Home';

    return normalized
        .split('/')
        .map((segment) =>
            segment
                .split('-')
                .map(
                    (part) =>
                        part.charAt(0).toUpperCase() +
                        part.slice(1).toLowerCase()
                )
                .join(' ')
        )
        .join(' / ');
}

function extractPageDisplayName(pageJson) {
    if (!pageJson || typeof pageJson !== 'object') return '';

    const candidates = [pageJson.PageName, pageJson.pageName];
    for (const candidate of candidates) {
        if (typeof candidate !== 'string') continue;
        const trimmed = candidate.trim();
        if (trimmed) return trimmed;
    }

    return '';
}

function getPageDisplayNameKey(pageJson) {
    if (!pageJson || typeof pageJson !== 'object') return 'PageName';
    if (Object.prototype.hasOwnProperty.call(pageJson, 'PageName')) {
        return 'PageName';
    }
    if (Object.prototype.hasOwnProperty.call(pageJson, 'pageName')) {
        return 'pageName';
    }
    return 'PageName';
}

function toOrderedPageList(pages, currentPageName) {
    const fromApi = Array.isArray(pages)
        ? pages
              .map((page) => normalizePageName(page))
              .filter(isVisibleSitePagesMenuPage)
        : [];

    const safeCurrentPage = isVisibleSitePagesMenuPage(currentPageName)
        ? normalizePageName(currentPageName)
        : 'index';

    const seen = new Set();
    const unique = ['index', ...fromApi, safeCurrentPage].filter((page) => {
        if (seen.has(page)) return false;
        seen.add(page);
        return true;
    });
    const nonHome = unique.filter((page) => page !== 'index');

    return ['index', ...nonHome];
}

function toPageMenuTree(pages) {
    const rootPages = [];
    const folderMap = new Map();

    pages.forEach((page) => {
        if (page === 'index') {
            rootPages.push(page);
            return;
        }

        const parts = page.split('/').filter(Boolean);
        if (parts.length <= 1) {
            rootPages.push(page);
            return;
        }

        const [folder, ...rest] = parts;
        if (!folder) return;

        const children = folderMap.get(folder) || [];
        children.push({
            path: page,
            relativePath: rest.join('/'),
        });
        folderMap.set(folder, children);
    });

    const folderNames = new Set(folderMap.keys());
    const visibleRootPages = rootPages.filter(
        (page) => page === 'index' || !folderNames.has(page)
    );

    const folders = Array.from(folderMap.entries()).map(
        ([folder, children]) => ({
            folder,
            children,
        })
    );

    return { rootPages: visibleRootPages, folders };
}

function BreakpointButton({ icon, title, isActive, onClick }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className={`flex h-7 w-7 items-center justify-center rounded-full transition-all ${
                isActive
                    ? 'bg-white text-primary-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
            }`}
        >
            {icon}
        </button>
    );
}

function getBreakpointFromWidth(width) {
    if (width <= 640) return 'mobile';
    if (width <= 1024) return 'tablet';
    return 'desktop';
}

/* ───────────────────── Component ───────────────────── */

export default function GlobalEditor({
    activeBreakpoint = 'desktop',
    onBreakpointClick = () => {},
    onAutoBreakpointChange = () => {},
}) {
    const editor = useEditor();
    const notifications = useNotifications();
    const { company_id } = editor.global.data;
    const [isDeletePageOpen, setIsDeletePageOpen] = useState(false);
    const [deleteTargetPage, setDeleteTargetPage] = useState(null);
    const [deleteTargetType, setDeleteTargetType] = useState('page');
    const [deleteEditorPage, setDeleteEditorPage] = useState(null);
    const [isDeletingTargetPage, setIsDeletingTargetPage] = useState(false);
    const [duplicatingPage, setDuplicatingPage] = useState(null);
    const [duplicateEditorPage, setDuplicateEditorPage] = useState(null);
    const [duplicateTargetInput, setDuplicateTargetInput] = useState('');
    const [renamingPage, setRenamingPage] = useState(null);
    const [renameEditorPage, setRenameEditorPage] = useState(null);
    const [renameTargetInput, setRenameTargetInput] = useState('');
    const [renamingFolder, setRenamingFolder] = useState(null);
    const [renameFolderEditor, setRenameFolderEditor] = useState(null);
    const [renameFolderInput, setRenameFolderInput] = useState('');
    const [editingPageNamePage, setEditingPageNamePage] = useState(null);
    const [editingPageNameInput, setEditingPageNameInput] = useState('');
    const [updatingPageNamePage, setUpdatingPageNamePage] = useState(null);
    const [createPageFolderEditor, setCreatePageFolderEditor] = useState(null);
    const [createPageFolderName, setCreatePageFolderName] = useState('');
    const [createPageFolderInput, setCreatePageFolderInput] = useState('');
    const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderFirstPage, setNewFolderFirstPage] = useState('');
    const [newFolderTemplate, setNewFolderTemplate] = useState(
        NEW_PAGE_TEMPLATE_OPTIONS[0].value
    );
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [isNewPageOpen, setIsNewPageOpen] = useState(false);
    const [newPageName, setNewPageName] = useState('');
    const [newPagePath, setNewPagePath] = useState('');
    const [newPageTemplate, setNewPageTemplate] = useState(
        NEW_PAGE_TEMPLATE_OPTIONS[0].value
    );
    const [isCreatingPage, setIsCreatingPage] = useState(false);
    const [updatingPublishedPage, setUpdatingPublishedPage] = useState(null);
    const [updatingNoIndexPage, setUpdatingNoIndexPage] = useState(null);
    const [isPageMenuOpen, setIsPageMenuOpen] = useState(false);
    const [pageList, setPageList] = useState(['index']);
    const [pageStatusByPath, setPageStatusByPath] = useState({});
    const [pageJsonByPath, setPageJsonByPath] = useState({});
    const [expandedFolders, setExpandedFolders] = useState({});
    const [draggingPage, setDraggingPage] = useState(null);
    const [dragOverTarget, setDragOverTarget] = useState(null);
    const [isLoadingPageList, setIsLoadingPageList] = useState(false);
    const [pageListError, setPageListError] = useState('');
    const [isSavingPageOrder, setIsSavingPageOrder] = useState(false);
    const [isPageOrderDirty, setIsPageOrderDirty] = useState(false);
    const pathname = usePathname();
    const router = useRouter();
    const searchParams = useSearchParams();
    const dragPreviewRef = useRef(null);
    const savedPageOrderSignatureRef = useRef('');

    // Track the last auto-detected breakpoint.
    const lastAutoBpRef = useRef(null);

    const pagename = normalizePageName(pathname);
    const visiblePageList = useMemo(() => {
        return pageList.filter((page) => {
            const normalized = normalizePageName(page);
            if (normalized === 'index' || normalized.includes('/')) return true;

            const hasChildren = pageList.some((candidate) =>
                normalizePageName(candidate).startsWith(`${normalized}/`)
            );
            if (hasChildren) return true;
            return true;
        });
    }, [pageList]);

    const pageMenuTree = useMemo(
        () => toPageMenuTree(visiblePageList),
        [visiblePageList]
    );
    const persistedPageOrder = useMemo(
        () =>
            normalizeStoredPageOrder(
                editor.global.data?.[PAGE_MENU_ORDER_GLOBAL_KEY]
            ),
        [editor.global.data?.[PAGE_MENU_ORDER_GLOBAL_KEY]]
    );
    const getPageDisplayName = (page, fallbackValue = page) => {
        const normalizedPage = normalizePageName(page);
        const currentPageData =
            normalizedPage === pagename ? editor.page.data : null;
        const pageData = pageJsonByPath[normalizedPage] || currentPageData;
        return (
            extractPageDisplayName(pageData) || formatPageName(fallbackValue)
        );
    };

    const syncSitemapConfigWithTemplate = async () => {
        try {
            await API.post(`${company_id}/sitemap/update`);
        } catch (error) {
            notifications.push({
                title: 'Publish status saved, but sitemap sync failed',
                message:
                    'Please run Site Settings > Update Sitemap Config, then deploy again.',
                variant: 'error',
                ttl: 15,
                error,
            });
        }
    };

    /* Auto-update the active breakpoint based on preview width.
       Preview width = viewport width - body margin-right (set by docked drawer).
       This must not use the click handler: changing preview modes remounts the
       iframe, so doing that during a browser resize can create a reload loop
       around a breakpoint boundary.
    */
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const getPreviewWidth = () => {
            const body = document.body;
            if (!body) return window.innerWidth;

            const computed = window.getComputedStyle(body);
            const marginRight = parseFloat(computed.marginRight || '0') || 0;

            return window.innerWidth - marginRight;
        };

        const update = () => {
            const previewWidth = getPreviewWidth();
            const bp = getBreakpointFromWidth(previewWidth);

            // Avoid repeated calls if breakpoint did not change.
            if (lastAutoBpRef.current !== bp) {
                lastAutoBpRef.current = bp;
                onAutoBreakpointChange(bp);
            }
        };

        // initial (au mount)
        update();

        const handleResize = () => update();
        window.addEventListener('resize', handleResize);

        // Poll briefly to catch body margin-right changes.
        const intervalId = window.setInterval(update, 150);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.clearInterval(intervalId);
        };
    }, [onAutoBreakpointChange]);

    useEffect(() => {
        setIsPageMenuOpen(false);
        setDeleteEditorPage(null);
        setDuplicateEditorPage(null);
        setDuplicateTargetInput('');
        setRenameEditorPage(null);
        setRenameTargetInput('');
        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setCreatePageFolderName('');
        setCreatePageFolderEditor(null);
        setCreatePageFolderInput('');
        setIsNewFolderOpen(false);
        setNewFolderName('');
        setNewFolderFirstPage('');
        setNewFolderTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
        setIsNewPageOpen(false);
        setNewPageName('');
        setNewPagePath('');
        setNewPageTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
        setIsPageOrderDirty(false);
    }, [pathname]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const raw = window.sessionStorage.getItem(PAGE_MENU_PENDING_ACTION_KEY);
        if (!raw) return;

        try {
            const pending = JSON.parse(raw);
            if (
                pending?.action === 'open-settings' &&
                normalizePageName(pending?.page) === pagename
            ) {
                window.sessionStorage.removeItem(PAGE_MENU_PENDING_ACTION_KEY);
                editor.page.open({ mode: 'settings' });
            }
        } catch {
            window.sessionStorage.removeItem(PAGE_MENU_PENDING_ACTION_KEY);
        }
    }, [pagename, editor.page]);

    useEffect(() => {
        if (!isPageMenuOpen || !company_id) return;

        let active = true;
        const loadPages = async () => {
            setIsLoadingPageList(true);
            setPageListError('');

            let apiPages = [];
            let recursiveApiPages = [];
            let siteRoutePages = [];
            let cachedPages = [];
            let apiError = null;
            let recursiveApiError = null;
            let siteRoutesError = null;

            cachedPages = readCachedPages(company_id);

            try {
                recursiveApiPages = await fetchEditorApiPages(company_id, {
                    recursive: true,
                });
            } catch (error) {
                recursiveApiError = error;
                console.error(error);
            }

            try {
                apiPages = await fetchEditorApiPages(company_id);
            } catch (error) {
                apiError = error;
                console.error(error);
            }

            try {
                siteRoutePages = await fetchSiteRoutePages();
            } catch (error) {
                siteRoutesError = error;
                console.error(error);
            }

            if (!active) return;

            const authoritativePages = [
                ...siteRoutePages,
                ...recursiveApiPages,
                ...apiPages,
            ];
            const authoritativeSet = new Set(
                authoritativePages.map((page) => normalizePageName(page))
            );
            const persistedAuthoritativePages = persistedPageOrder.filter(
                (page) => authoritativeSet.has(normalizePageName(page))
            );
            const cachedAuthoritativePages = cachedPages.filter((page) =>
                authoritativeSet.has(normalizePageName(page))
            );
            const preferredOrderedPages = persistedAuthoritativePages.length
                ? persistedAuthoritativePages
                : cachedAuthoritativePages;
            const mergedPages = authoritativePages.length
                ? [...preferredOrderedPages, ...authoritativePages]
                : [...cachedPages, ...authoritativePages];
            const savedSignature = getPageOrderSignature(
                toOrderedPageList(
                    [...persistedAuthoritativePages, ...authoritativePages],
                    pagename
                )
            );
            savedPageOrderSignatureRef.current = savedSignature;
            setIsPageOrderDirty(false);
            setPageList((prev) =>
                toOrderedPageList([...mergedPages, ...prev], pagename)
            );

            if (
                !mergedPages.length &&
                (apiError || recursiveApiError || siteRoutesError)
            ) {
                if (!active) return;
                setPageListError('Unable to load pages right now.');
            }

            if (active) setIsLoadingPageList(false);
        };

        loadPages();

        return () => {
            active = false;
        };
    }, [isPageMenuOpen, company_id, pagename, persistedPageOrder]);

    useEffect(() => {
        if (!company_id || !Array.isArray(pageList) || !pageList.length) return;
        if (!isPageMenuOpen || isLoadingPageList) return;
        writeCachedPages(company_id, pageList);
    }, [company_id, pageList, isPageMenuOpen, isLoadingPageList]);

    useEffect(() => {
        if (!isPageMenuOpen || !pageList.length) return;

        let active = true;
        const loadStatuses = async () => {
            const statusEntries = await Promise.all(
                pageList.map(async (page) => {
                    try {
                        const response = await fetch(
                            getStaticJsonPath(page, 'page.json'),
                            {
                                cache: 'no-store',
                            }
                        );

                        if (!response.ok) {
                            throw new Error(
                                `Status fetch failed for ${page}: ${response.status}`
                            );
                        }

                        const payload = await response.json();
                        return {
                            page,
                            status: {
                                published: parsePublishedValue(
                                    payload?.Published
                                ),
                                noindex: parseNoIndexValue(payload?.NoIndex),
                                error: false,
                            },
                            pageJson: payload,
                        };
                    } catch (error) {
                        return {
                            page,
                            status: {
                                published: true,
                                noindex: false,
                                error: true,
                            },
                            pageJson: null,
                        };
                    }
                })
            );

            if (!active) return;
            setPageStatusByPath((prev) => ({
                ...prev,
                ...Object.fromEntries(
                    statusEntries.map(({ page, status }) => [page, status])
                ),
            }));
            setPageJsonByPath((prev) => ({
                ...prev,
                ...Object.fromEntries(
                    statusEntries
                        .filter(({ pageJson }) => !!pageJson)
                        .map(({ page, pageJson }) => [page, pageJson])
                ),
            }));
        };

        loadStatuses();

        return () => {
            active = false;
        };
    }, [isPageMenuOpen, pageList]);

    useEffect(() => {
        setExpandedFolders((prev) => {
            let changed = false;
            const next = {};

            pageMenuTree.folders.forEach(({ folder, children }) => {
                const hasCurrent = children.some(
                    (child) => child.path === pagename
                );
                const previous = prev[folder];
                const value =
                    typeof previous === 'boolean' ? previous : hasCurrent;
                next[folder] = hasCurrent ? true : value;

                if (next[folder] !== previous) changed = true;
            });

            if (
                !changed &&
                Object.keys(prev).length === Object.keys(next).length
            ) {
                return prev;
            }

            return next;
        });
    }, [pageMenuTree, pagename]);

    const goToPage = ({ selectedPage, openSettings = false }) => {
        const nextPage = normalizePageName(selectedPage);

        if (nextPage === pagename) {
            if (openSettings) {
                setIsPageMenuOpen(false);
                editor.page.open({ mode: 'settings' });
            } else {
                setIsPageMenuOpen(false);
            }
            return;
        }

        if (
            editor.has_updates &&
            typeof window !== 'undefined' &&
            !window.confirm(
                'You have unsaved changes. Switch page without saving?'
            )
        ) {
            return;
        }

        if (openSettings && typeof window !== 'undefined') {
            window.sessionStorage.setItem(
                PAGE_MENU_PENDING_ACTION_KEY,
                JSON.stringify({
                    action: 'open-settings',
                    page: nextPage,
                })
            );
        }

        const targetPath = pageNameToPath(nextPage);
        const queryString = searchParams?.toString() || '';
        const targetUrl = queryString
            ? `${targetPath}?${queryString}`
            : targetPath;
        router.push(targetUrl);
    };

    const handlePageSelect = (selectedPage) => {
        goToPage({ selectedPage, openSettings: false });
    };

    const handleRootPageSelect = async (selectedPage) => {
        const normalizedPage = normalizePageName(selectedPage);

        if (normalizedPage === 'index') {
            handlePageSelect(normalizedPage);
            return;
        }

        const hasLoadedChildren = pageList.some((page) =>
            normalizePageName(page).startsWith(`${normalizedPage}/`)
        );
        if (hasLoadedChildren) return;

        try {
            const recursivePages = await fetchEditorApiPages(company_id, {
                recursive: true,
            });

            if (recursivePages.length) {
                const merged = toOrderedPageList(
                    [...pageList, ...recursivePages],
                    pagename
                );
                setPageList(merged);

                const nowHasChildren = merged.some((page) =>
                    normalizePageName(page).startsWith(`${normalizedPage}/`)
                );
                if (nowHasChildren) {
                    setExpandedFolders((prev) => ({
                        ...prev,
                        [normalizedPage]: true,
                    }));
                    return;
                }
            }
        } catch (error) {
            // Continue to fallback behavior below.
        }

        const status = pageStatusByPath[normalizedPage];
        if (status?.error) {
            // Avoid navigating to likely folder placeholders that do not have a real page route.
            return;
        }

        handlePageSelect(normalizedPage);
    };

    const toggleFolder = (folderName) => {
        setExpandedFolders((prev) => ({
            ...prev,
            [folderName]: !prev[folderName],
        }));
    };

    const toFolderSegment = (value) => {
        const normalized = finalizePagePath(value);
        return normalized.split('/').filter(Boolean)[0] || '';
    };

    const getTopLevelFolderForPage = (page) => {
        const normalized = normalizePageName(page);
        if (normalized === 'index') return null;
        const parts = normalized.split('/').filter(Boolean);
        return parts.length > 1 ? parts[0] : null;
    };

    const getSuffixForFolderMove = (page) => {
        const normalized = normalizePageName(page);
        const parts = normalized.split('/').filter(Boolean);
        if (!parts.length) return '';
        return parts.length > 1 ? parts.slice(1).join('/') : parts[0];
    };

    const buildMovedPagePath = (sourcePage, targetFolder) => {
        const suffix = getSuffixForFolderMove(sourcePage);
        if (!suffix) return '';
        return targetFolder ? `${targetFolder}/${suffix}` : suffix;
    };

    const findLastIndex = (items, predicate) => {
        for (let index = items.length - 1; index >= 0; index -= 1) {
            if (predicate(items[index], index)) return index;
        }
        return -1;
    };

    const placePageAtEndOfRoot = (pages, pageToInsert) => {
        const lastRootIndex = findLastIndex(
            pages,
            (page) => page === 'index' || !page.includes('/')
        );
        if (lastRootIndex < 0) return [...pages, pageToInsert];
        const next = [...pages];
        next.splice(lastRootIndex + 1, 0, pageToInsert);
        return next;
    };

    const placePageAtEndOfFolder = (pages, pageToInsert, folder) => {
        const folderPrefix = `${folder}/`;
        const lastFolderIndex = findLastIndex(pages, (page) =>
            normalizePageName(page).startsWith(folderPrefix)
        );
        if (lastFolderIndex < 0) return [...pages, pageToInsert];
        const next = [...pages];
        next.splice(lastFolderIndex + 1, 0, pageToInsert);
        return next;
    };

    const reorderPageBeforeTarget = (pages, pageToMove, targetPage) => {
        const normalizedTarget = normalizePageName(targetPage);
        const targetIndex = pages.findIndex(
            (page) => normalizePageName(page) === normalizedTarget
        );
        if (targetIndex < 0) return [...pages, pageToMove];
        const next = [...pages];
        next.splice(targetIndex, 0, pageToMove);
        return next;
    };

    const reorderPageAfterTarget = (pages, pageToMove, targetPage) => {
        const normalizedTarget = normalizePageName(targetPage);
        const targetIndex = pages.findIndex(
            (page) => normalizePageName(page) === normalizedTarget
        );
        if (targetIndex < 0) return [...pages, pageToMove];
        const next = [...pages];
        next.splice(targetIndex + 1, 0, pageToMove);
        return next;
    };

    const clearDragState = () => {
        setDraggingPage(null);
        setDragOverTarget(null);
        if (dragPreviewRef.current?.parentNode) {
            dragPreviewRef.current.parentNode.removeChild(
                dragPreviewRef.current
            );
        }
        dragPreviewRef.current = null;
    };

    const handlePageDragStart = (page, event) => {
        const sourcePage = normalizePageName(page);
        if (sourcePage === 'index') {
            event.preventDefault();
            return;
        }

        setDraggingPage(sourcePage);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('application/x-editor-page', sourcePage);
        event.dataTransfer.setData('text/plain', sourcePage);

        const target = event.currentTarget;
        if (
            typeof document !== 'undefined' &&
            target instanceof HTMLElement &&
            event.dataTransfer?.setDragImage
        ) {
            const rect = target.getBoundingClientRect();
            const preview = target.cloneNode(true);

            if (preview instanceof HTMLElement) {
                preview.style.position = 'fixed';
                preview.style.top = '-10000px';
                preview.style.left = '-10000px';
                preview.style.width = `${Math.ceil(rect.width)}px`;
                preview.style.height = `${Math.ceil(rect.height)}px`;
                preview.style.margin = '0';
                preview.style.pointerEvents = 'none';
                preview.style.borderRadius = '12px';
                preview.style.overflow = 'hidden';
                preview.style.backgroundClip = 'padding-box';
                preview.style.opacity = '0.98';
                preview.style.transform = 'none';
                preview.style.boxShadow = '0 8px 24px rgba(0,0,0,0.18)';

                if (dragPreviewRef.current?.parentNode) {
                    dragPreviewRef.current.parentNode.removeChild(
                        dragPreviewRef.current
                    );
                }
                document.body.appendChild(preview);
                dragPreviewRef.current = preview;
                const pointerOffsetX = Math.max(
                    0,
                    Math.min(rect.width, event.clientX - rect.left)
                );
                const pointerOffsetY = Math.max(
                    0,
                    Math.min(rect.height, event.clientY - rect.top)
                );
                event.dataTransfer.setDragImage(
                    preview,
                    pointerOffsetX,
                    pointerOffsetY
                );
            }
        }
    };

    const readDraggedPageFromEvent = (event) => {
        const normalizeDragged = (value) => {
            const raw = String(value || '').trim();
            if (!raw) return '';
            return normalizePageName(raw);
        };

        const explicit = event.dataTransfer.getData(
            'application/x-editor-page'
        );
        if (explicit) return normalizeDragged(explicit);

        const fallback = event.dataTransfer.getData('text/plain');
        if (fallback) return normalizeDragged(fallback);

        return normalizeDragged(draggingPage);
    };

    const savePageOrderToRepository = async (orderedPages) => {
        if (!company_id) return;

        const normalizedOrder = Array.isArray(orderedPages)
            ? orderedPages
                  .map((page) => normalizePageName(page))
                  .filter(isVisibleSitePagesMenuPage)
            : [];
        const signature = getPageOrderSignature(normalizedOrder);

        if (
            !signature ||
            signature === savedPageOrderSignatureRef.current ||
            isSavingPageOrder
        ) {
            setIsPageOrderDirty(false);
            return;
        }

        setIsSavingPageOrder(true);
        try {
            let latestGlobals = null;
            try {
                const response = await fetch('/static/json/globals.json', {
                    cache: 'no-store',
                });
                if (response.ok) {
                    latestGlobals = await response.json();
                }
            } catch {
                // Fallback to context data.
            }

            const baseGlobals =
                latestGlobals && typeof latestGlobals === 'object'
                    ? latestGlobals
                    : editor.global.data || {};

            await API.patch(`${company_id}/pages`, {
                page_name: 'index',
                data: {
                    components_to_update: [
                        {
                            name: 'globals',
                            data_file: 'globals.json',
                            props: {
                                ...baseGlobals,
                                [PAGE_MENU_ORDER_GLOBAL_KEY]: normalizedOrder,
                            },
                        },
                    ],
                    components_to_delete: [],
                },
            });

            savedPageOrderSignatureRef.current = signature;
            setIsPageOrderDirty(false);
            if (company_id) writeCachedPages(company_id, normalizedOrder);

            notifications.push({
                title: 'Page order saved!',
                message:
                    'Wait for the site to rebuild. Refresh after deployment to confirm.',
                variant: 'success',
                ttl: 8,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to save page order',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setIsSavingPageOrder(false);
        }
    };

    const applyReorderedPageList = ({
        sourcePage,
        movedPage,
        targetPage = null,
        targetFolder = null,
        insertAfter = false,
    }) => {
        const normalizedSource = normalizePageName(sourcePage);
        const normalizedMoved = normalizePageName(movedPage);
        const normalizedTarget = targetPage
            ? normalizePageName(targetPage)
            : null;

        setPageList((prev) => {
            const withoutSource = prev.filter((page) => {
                const normalized = normalizePageName(page);
                return (
                    normalized !== normalizedSource &&
                    normalized !== normalizedMoved
                );
            });

            if (normalizedTarget && normalizedTarget !== normalizedMoved) {
                const next = insertAfter
                    ? reorderPageAfterTarget(
                          withoutSource,
                          normalizedMoved,
                          normalizedTarget
                      )
                    : reorderPageBeforeTarget(
                          withoutSource,
                          normalizedMoved,
                          normalizedTarget
                      );
                if (company_id) writeCachedPages(company_id, next);
                setIsPageOrderDirty(
                    getPageOrderSignature(next) !==
                        savedPageOrderSignatureRef.current
                );
                return next;
            }

            if (targetFolder) {
                const next = placePageAtEndOfFolder(
                    withoutSource,
                    normalizedMoved,
                    targetFolder
                );
                if (company_id) writeCachedPages(company_id, next);
                setIsPageOrderDirty(
                    getPageOrderSignature(next) !==
                        savedPageOrderSignatureRef.current
                );
                return next;
            }

            const next = placePageAtEndOfRoot(withoutSource, normalizedMoved);
            if (company_id) writeCachedPages(company_id, next);
            setIsPageOrderDirty(
                getPageOrderSignature(next) !==
                    savedPageOrderSignatureRef.current
            );
            return next;
        });
    };

    const handleDropPageToTarget = async ({
        sourcePage,
        targetPage = null,
        targetFolder = null,
        insertAfter = false,
    }) => {
        const normalizedSource = normalizePageName(sourcePage);
        if (!normalizedSource || normalizedSource === 'index') {
            clearDragState();
            return;
        }
        if (
            targetPage &&
            normalizePageName(targetPage) === normalizedSource &&
            targetFolder == null
        ) {
            clearDragState();
            return;
        }

        const desiredFolder =
            targetFolder != null
                ? targetFolder
                : targetPage
                ? getTopLevelFolderForPage(targetPage)
                : null;
        const nextPath = buildMovedPagePath(normalizedSource, desiredFolder);

        if (!nextPath || nextPath === 'index') {
            clearDragState();
            return;
        }

        let movedPath = normalizedSource;
        const requiresPathMove = nextPath !== normalizedSource;
        if (requiresPathMove) {
            const destinationExists = pageList.some(
                (page) =>
                    normalizePageName(page) === nextPath &&
                    normalizePageName(page) !== normalizedSource
            );
            if (destinationExists) {
                notifications.push({
                    title: 'Page already exists',
                    message: `/${nextPath} already exists.`,
                    variant: 'error',
                    ttl: 8,
                });
                clearDragState();
                return;
            }

            const moved = await handleRenamePage(normalizedSource, nextPath, {
                silentSuccess: true,
            });
            if (!moved) {
                clearDragState();
                return;
            }
            movedPath = nextPath;
        }

        const targetForOrdering =
            targetPage && normalizePageName(targetPage) !== normalizedSource
                ? targetPage
                : null;

        applyReorderedPageList({
            sourcePage: normalizedSource,
            movedPage: movedPath,
            targetPage: targetForOrdering,
            targetFolder: desiredFolder,
            insertAfter,
        });
        clearDragState();
    };

    const handleDragOverTarget = (targetId, event) => {
        const sourcePage = readDraggedPageFromEvent(event);
        if (!sourcePage || sourcePage === 'index') return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (dragOverTarget !== targetId) setDragOverTarget(targetId);
    };

    const renderDropGap = (targetId, targetPage) => {
        if (!draggingPage || dragOverTarget !== targetId) return null;
        return (
            <div
                onDragOver={(event) => handleDragOverTarget(targetId, event)}
                onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const sourcePage = readDraggedPageFromEvent(event);
                    if (!sourcePage) {
                        clearDragState();
                        return;
                    }
                    void handleDropPageToTarget({
                        sourcePage,
                        targetPage,
                    });
                }}
                className="bg-primary-50/70 h-[72px] rounded-xl border-2 border-dashed border-primary-300 transition-all"
            />
        );
    };

    const renderTrailingDropZone = (targetId, { targetFolder = null } = {}) => {
        if (!draggingPage) return null;
        const isActive = dragOverTarget === targetId;
        return (
            <div
                onDragOver={(event) => handleDragOverTarget(targetId, event)}
                onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const sourcePage = readDraggedPageFromEvent(event);
                    if (!sourcePage) {
                        clearDragState();
                        return;
                    }
                    void handleDropPageToTarget({
                        sourcePage,
                        targetFolder,
                    });
                }}
                className={`rounded-xl border-2 border-dashed transition-all ${
                    isActive
                        ? 'bg-primary-50/70 h-[72px] border-primary-300'
                        : 'h-3 border-transparent'
                }`}
            />
        );
    };

    const openPageSettingsFromMenu = (selectedPage) => {
        goToPage({ selectedPage, openSettings: true });
    };

    const openDeleteTargetPageFromMenu = (selectedPage) => {
        const targetPage = normalizePageName(selectedPage);
        if (targetPage === 'index') return;

        if (deleteEditorPage === targetPage) {
            setDeleteEditorPage(null);
            return;
        }

        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setCreatePageFolderName('');
        setCreatePageFolderEditor(null);
        setCreatePageFolderInput('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setDuplicateEditorPage(null);
        setDuplicateTargetInput('');
        setRenameEditorPage(null);
        setRenameTargetInput('');
        setDeleteEditorPage(targetPage);
    };

    const openDeleteTargetFolderFromMenu = (selectedFolder) => {
        const targetFolder = toFolderSegment(selectedFolder);
        if (!targetFolder) return;

        setDeleteEditorPage(null);
        setIsPageMenuOpen(false);
        setDeleteTargetPage(targetFolder);
        setDeleteTargetType('folder');
        setIsDeletePageOpen(true);
    };

    const closeDeletePageModal = () => {
        if (isDeletingTargetPage) return;
        setIsDeletePageOpen(false);
        setDeleteTargetPage(null);
        setDeleteTargetType('page');
    };

    const closeDeletePageInlineEditor = () => {
        if (isDeletingTargetPage) return;
        setDeleteEditorPage(null);
    };

    const handleConfirmDeletePage = async ({
        targetPageInput = null,
        targetTypeInput = null,
        fromInline = false,
    } = {}) => {
        const targetPage = normalizePageName(
            targetPageInput || deleteTargetPage || pagename
        );
        const targetType = targetTypeInput || deleteTargetType;
        const isFolderDelete = targetType === 'folder';
        const folderPrefix = `${targetPage}/`;
        const hasStandaloneTarget = pageList.some(
            (page) => normalizePageName(page) === targetPage
        );
        const folderChildren = pageList
            .map((page) => normalizePageName(page))
            .filter((page) => page.startsWith(folderPrefix));
        const pathsToDelete = isFolderDelete
            ? [...(hasStandaloneTarget ? [targetPage] : []), ...folderChildren]
            : [targetPage];

        // Keep existing editor-context behavior for the active page.
        if (!isFolderDelete && targetPage === pagename) {
            await editor.page.delete();
            setDeleteEditorPage(null);
            if (!fromInline) {
                setIsDeletePageOpen(false);
                setDeleteTargetPage(null);
                setDeleteTargetType('page');
            }
            return;
        }

        if (isFolderDelete && pathsToDelete.includes(pagename)) {
            notifications.push({
                title: 'Cannot delete active folder',
                message: 'Switch to a page outside this folder and try again.',
                variant: 'error',
                ttl: 10,
            });
            return;
        }

        if (isFolderDelete && !pathsToDelete.length) {
            notifications.push({
                title: 'Folder not found',
                message: `No pages were found in /${targetPage}.`,
                variant: 'error',
                ttl: 10,
            });
            return;
        }

        const normalizedDeleteSet = new Set(pathsToDelete);
        const applyLocalDeleteState = () => {
            setPageList((prev) =>
                prev.filter((page) => {
                    const normalized = normalizePageName(page);
                    return (
                        !normalizedDeleteSet.has(normalized) &&
                        !normalized.startsWith(`${targetPage}/`)
                    );
                })
            );
            setPageStatusByPath((prev) =>
                Object.fromEntries(
                    Object.entries(prev).filter(([page]) => {
                        const normalized = normalizePageName(page);
                        return (
                            !normalizedDeleteSet.has(normalized) &&
                            !normalized.startsWith(`${targetPage}/`)
                        );
                    })
                )
            );
            setPageJsonByPath((prev) =>
                Object.fromEntries(
                    Object.entries(prev).filter(([page]) => {
                        const normalized = normalizePageName(page);
                        return (
                            !normalizedDeleteSet.has(normalized) &&
                            !normalized.startsWith(`${targetPage}/`)
                        );
                    })
                )
            );
            setExpandedFolders((prev) => {
                const next = { ...prev };
                delete next[targetPage];
                return next;
            });
            setDeleteEditorPage((prev) =>
                prev === targetPage || isFolderDelete ? null : prev
            );
            if (!fromInline) {
                setIsDeletePageOpen(false);
                setDeleteTargetPage(null);
                setDeleteTargetType('page');
            }
        };

        // Optimistic remove: hide cards immediately after confirm.
        applyLocalDeleteState();
        setIsDeletingTargetPage(true);
        try {
            for (const pagePath of pathsToDelete) {
                try {
                    await API.delete(`${company_id}/pages`, {
                        path: pagePath,
                    });
                } catch (error) {
                    const statusCode = Number(error?.status);
                    const notFound = statusCode === 404;
                    const unknownStatus = !!pageStatusByPath[pagePath]?.error;
                    if (notFound || unknownStatus) continue;

                    const transientStatusCodes = new Set([
                        408, 409, 422, 423, 425, 429, 500, 502, 503, 504,
                    ]);
                    const isTransientDeleteError =
                        !statusCode || transientStatusCodes.has(statusCode);

                    if (isTransientDeleteError) {
                        let retrySucceeded = false;
                        let lastDeleteError = error;

                        for (let attempt = 0; attempt < 3; attempt += 1) {
                            if (attempt > 0) {
                                await new Promise((resolve) =>
                                    window.setTimeout(resolve, 250 * attempt)
                                );
                            }

                            const latestPages = await fetchEditorApiPages(
                                company_id,
                                { recursive: true }
                            ).catch(() => []);
                            const latestSet = new Set(
                                latestPages.map((page) =>
                                    normalizePageName(page)
                                )
                            );
                            const normalizedPath = normalizePageName(pagePath);
                            const stillPresent =
                                latestSet.has(normalizedPath) ||
                                Array.from(latestSet).some((page) =>
                                    page.startsWith(`${normalizedPath}/`)
                                );

                            if (!stillPresent) {
                                retrySucceeded = true;
                                break;
                            }

                            try {
                                await API.delete(`${company_id}/pages`, {
                                    path: pagePath,
                                });
                                retrySucceeded = true;
                                break;
                            } catch (retryError) {
                                lastDeleteError = retryError;
                            }
                        }

                        if (retrySucceeded) continue;
                        throw lastDeleteError;
                    }

                    throw error;
                }
            }

            notifications.push({
                title: isFolderDelete
                    ? 'Folder successfully deleted!'
                    : 'Page successfully deleted!',
                message:
                    'Wait for the site to be rebuilt and refresh the page list.',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            const latestPages = await fetchEditorApiPages(company_id, {
                recursive: true,
            }).catch(() => []);
            const latestSet = new Set(
                latestPages.map((page) => normalizePageName(page))
            );
            const stillPresent = pathsToDelete.some((path) => {
                const normalizedPath = normalizePageName(path);
                return (
                    latestSet.has(normalizedPath) ||
                    Array.from(latestSet).some((page) =>
                        page.startsWith(`${normalizedPath}/`)
                    )
                );
            });

            if (stillPresent) {
                notifications.push({
                    title: isFolderDelete
                        ? 'Delete request delayed'
                        : 'Delete request delayed',
                    message:
                        'Removed from the menu locally. If it reappears after refresh, retry delete once.',
                    variant: 'error',
                    ttl: 20,
                    error,
                });
            } else {
                notifications.push({
                    title: isFolderDelete
                        ? 'Folder successfully deleted!'
                        : 'Page successfully deleted!',
                    message:
                        'Delete completed in the background. Refresh after deployment.',
                    variant: 'success',
                    ttl: 10,
                });
            }
        } finally {
            setIsDeletingTargetPage(false);
        }
    };

    const openAddPageFromMenu = () => {
        setDeleteEditorPage(null);
        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setCreatePageFolderName('');
        setCreatePageFolderEditor(null);
        setCreatePageFolderInput('');
        setIsNewPageOpen((prev) => {
            const next = !prev;
            if (next) {
                setIsNewFolderOpen(false);
                setNewFolderName('');
                setNewFolderFirstPage('');
                setNewFolderTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
                setNewPageName('');
                setNewPagePath('');
                setNewPageTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
            }
            return next;
        });
    };

    const openAddPageInFolderFromMenu = (folder) => {
        const folderSegment = toFolderSegment(folder);
        if (!folderSegment) return;

        if (createPageFolderEditor === folderSegment) {
            setCreatePageFolderEditor(null);
            setCreatePageFolderName('');
            setCreatePageFolderInput('');
            return;
        }

        setDeleteEditorPage(null);
        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setCreatePageFolderName('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setIsNewPageOpen(false);
        setNewPageName('');
        setNewPagePath('');
        setCreatePageFolderEditor(folderSegment);
        setCreatePageFolderName('');
        setCreatePageFolderInput('');
        setIsNewFolderOpen(false);
        setNewFolderName('');
        setNewFolderFirstPage('');
        setNewFolderTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
        setNewPageTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
        setExpandedFolders((prev) => ({
            ...prev,
            [folderSegment]: true,
        }));
    };

    const closeNewPageFromMenu = () => {
        if (isCreatingPage) return;
        setIsNewPageOpen(false);
        setNewPageName('');
        setNewPagePath('');
        setNewPageTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
    };

    const openNewFolderFromMenu = () => {
        setDeleteEditorPage(null);
        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setCreatePageFolderName('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setCreatePageFolderEditor(null);
        setCreatePageFolderInput('');
        setIsNewFolderOpen((prev) => {
            const next = !prev;
            if (next) {
                setIsNewPageOpen(false);
                setNewPageName('');
                setNewPagePath('');
                setNewPageTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
                setNewFolderName('');
                setNewFolderFirstPage('');
                setNewFolderTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
            }
            return next;
        });
    };

    const closeNewFolderFromMenu = () => {
        if (isCreatingFolder) return;
        setIsNewFolderOpen(false);
        setNewFolderName('');
        setNewFolderFirstPage('');
        setNewFolderTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
    };

    const savePageNameValue = async (selectedPage, nextName) => {
        const page = normalizePageName(selectedPage);
        const trimmedName = String(nextName || '').trim();

        let pageData = pageJsonByPath[page] || null;
        if (!pageData && page === pagename && editor.page.data) {
            pageData = editor.page.data;
        }
        if (!pageData) {
            const response = await fetch(getStaticJsonPath(page, 'page.json'), {
                cache: 'no-store',
            });

            if (!response.ok) {
                throw new Error(
                    `Failed to load page data (${response.status}) for ${page}`
                );
            }

            pageData = await response.json();
        }

        const pageNameKey = getPageDisplayNameKey(pageData);
        const nextPageData = {
            ...pageData,
            [pageNameKey]: trimmedName,
        };

        await API.patch(`${company_id}/pages`, {
            page_name: page,
            data: {
                components_to_update: [
                    {
                        name: 'page',
                        data_file: 'page.json',
                        props: nextPageData,
                    },
                ],
                components_to_delete: [],
            },
        });

        setPageJsonByPath((prev) => ({
            ...prev,
            [page]: nextPageData,
        }));

        return nextPageData;
    };

    const handleCreateFolderFromMenu = async () => {
        const folderSegment = finalizePagePath(newFolderName)
            .split('/')
            .filter(Boolean)[0];
        const firstPageSegment = finalizePagePath(newFolderFirstPage)
            .split('/')
            .filter(Boolean)[0];

        if (!folderSegment || !firstPageSegment) {
            notifications.push({
                title: 'Invalid folder path',
                message: 'Provide a folder name and first page slug to create.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (folderSegment === 'index') {
            notifications.push({
                title: 'Invalid folder name',
                message: 'The folder name cannot be "index".',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        const targetPage = `${folderSegment}/${firstPageSegment}`;
        if (!isSelectableEditorPage(targetPage)) {
            notifications.push({
                title: 'Invalid target path',
                message:
                    'Use only letters, numbers, dashes, and slashes for folder/page.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (
            pageList.some((page) => normalizePageName(page) === targetPage) ||
            targetPage === pagename
        ) {
            notifications.push({
                title: 'Page already exists',
                message: `/${targetPage} already exists.`,
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        setIsCreatingFolder(true);
        try {
            await API.post(`${company_id}/pages`, {
                page: newFolderTemplate || NEW_PAGE_TEMPLATE_OPTIONS[0].value,
                path: targetPage,
            });

            setPageList((prev) =>
                toOrderedPageList([...prev, targetPage], pagename)
            );
            setExpandedFolders((prev) => ({
                ...prev,
                [folderSegment]: true,
            }));
            setPageStatusByPath((prev) => ({
                ...prev,
                [targetPage]: {
                    published: true,
                    noindex: false,
                    error: false,
                },
            }));
            setPageJsonByPath((prev) => ({
                ...prev,
                [targetPage]: {
                    ...(prev[targetPage] || {}),
                    Published: true,
                    NoIndex: false,
                },
            }));

            notifications.push({
                title: 'Folder created!',
                message: `Created /${targetPage}.`,
                variant: 'success',
                ttl: 10,
            });

            setIsNewFolderOpen(false);
            setNewFolderName('');
            setNewFolderFirstPage('');
            setNewFolderTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
        } catch (error) {
            notifications.push({
                title: 'Failed to create folder',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setIsCreatingFolder(false);
        }
    };

    const handleCreatePageFromMenu = async () => {
        const nextPageName = String(newPageName || '').trim();
        if (!nextPageName) {
            notifications.push({
                title: 'Invalid page name',
                message: 'Please enter a page name.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        const targetPage = finalizePagePath(newPagePath);
        if (!targetPage || targetPage === 'index') {
            notifications.push({
                title: 'Invalid page URL',
                message: 'Please enter a valid page slug/path.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (!isSelectableEditorPage(targetPage)) {
            notifications.push({
                title: 'Invalid target path',
                message:
                    'Use only letters, numbers, dashes, and slashes for page URL.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (
            pageList.some((page) => normalizePageName(page) === targetPage) ||
            targetPage === pagename
        ) {
            notifications.push({
                title: 'Page already exists',
                message: `/${targetPage} already exists.`,
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        setIsCreatingPage(true);
        try {
            await API.post(`${company_id}/pages`, {
                page: newPageTemplate,
                path: targetPage,
            });

            let pageNameSyncError = null;
            try {
                await savePageNameValue(targetPage, nextPageName);
            } catch (error) {
                pageNameSyncError = error;
                console.error(error);
            }

            setPageList((prev) =>
                toOrderedPageList([...prev, targetPage], pagename)
            );

            const folderName = targetPage.split('/')[0];
            if (targetPage.includes('/') && folderName) {
                setExpandedFolders((prev) => ({
                    ...prev,
                    [folderName]: true,
                }));
            }

            setPageStatusByPath((prev) => ({
                ...prev,
                [targetPage]: {
                    published: true,
                    noindex: false,
                    error: false,
                },
            }));

            if (pageNameSyncError) {
                notifications.push({
                    title: 'Page created, name sync failed',
                    message:
                        'The page was created but page name could not be saved. Please retry.',
                    variant: 'error',
                    ttl: 20,
                    error: pageNameSyncError,
                });
            } else {
                notifications.push({
                    title: 'Page created!',
                    message: `Created /${targetPage}.`,
                    variant: 'success',
                    ttl: 10,
                });
            }

            setIsNewPageOpen(false);
            setNewPageName('');
            setNewPagePath('');
            setNewPageTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
        } catch (error) {
            notifications.push({
                title: 'Failed to create page',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setIsCreatingPage(false);
        }
    };

    const closeCreatePageInFolderInlineEditor = () => {
        if (isCreatingPage) return;
        setCreatePageFolderEditor(null);
        setCreatePageFolderName('');
        setCreatePageFolderInput('');
    };

    const handleCreatePageInFolderFromMenu = async (folder) => {
        const folderSegment = toFolderSegment(folder);
        if (!folderSegment) return;

        const nextPageName = String(createPageFolderName || '').trim();
        if (!nextPageName) {
            notifications.push({
                title: 'Invalid page name',
                message: 'Please enter a page name.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        const rawInput = finalizePagePath(createPageFolderInput);
        const targetPage = rawInput.startsWith(`${folderSegment}/`)
            ? rawInput
            : finalizePagePath(`${folderSegment}/${rawInput}`);

        if (
            !targetPage ||
            targetPage === 'index' ||
            targetPage === folderSegment
        ) {
            notifications.push({
                title: 'Invalid page URL',
                message: 'Please enter a valid page slug/path.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (!isSelectableEditorPage(targetPage)) {
            notifications.push({
                title: 'Invalid target path',
                message:
                    'Use only letters, numbers, dashes, and slashes for page URL.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (
            pageList.some((page) => normalizePageName(page) === targetPage) ||
            targetPage === pagename
        ) {
            notifications.push({
                title: 'Page already exists',
                message: `/${targetPage} already exists.`,
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        setIsCreatingPage(true);
        try {
            await API.post(`${company_id}/pages`, {
                page: newPageTemplate,
                path: targetPage,
            });

            let pageNameSyncError = null;
            try {
                await savePageNameValue(targetPage, nextPageName);
            } catch (error) {
                pageNameSyncError = error;
                console.error(error);
            }

            setPageList((prev) =>
                toOrderedPageList([...prev, targetPage], pagename)
            );
            setExpandedFolders((prev) => ({
                ...prev,
                [folderSegment]: true,
            }));
            setPageStatusByPath((prev) => ({
                ...prev,
                [targetPage]: {
                    published: true,
                    noindex: false,
                    error: false,
                },
            }));

            if (pageNameSyncError) {
                notifications.push({
                    title: 'Page created, name sync failed',
                    message:
                        'The page was created but page name could not be saved. Please retry.',
                    variant: 'error',
                    ttl: 20,
                    error: pageNameSyncError,
                });
            } else {
                notifications.push({
                    title: 'Page created!',
                    message: `Created /${targetPage}.`,
                    variant: 'success',
                    ttl: 10,
                });
            }

            setCreatePageFolderEditor(null);
            setCreatePageFolderName('');
            setCreatePageFolderInput('');
            setNewPageTemplate(NEW_PAGE_TEMPLATE_OPTIONS[0].value);
        } catch (error) {
            notifications.push({
                title: 'Failed to create page',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setIsCreatingPage(false);
        }
    };

    const handleTogglePublishedFromMenu = async (
        selectedPage,
        event = null
    ) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const page = normalizePageName(selectedPage);
        if (updatingPublishedPage) return;

        setUpdatingPublishedPage(page);
        try {
            let pageData = pageJsonByPath[page] || null;
            if (!pageData) {
                const response = await fetch(
                    getStaticJsonPath(page, 'page.json'),
                    {
                        cache: 'no-store',
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Failed to load page data (${response.status}) for ${page}`
                    );
                }

                pageData = await response.json();
            }

            const currentPublished =
                typeof pageStatusByPath[page]?.published === 'boolean'
                    ? pageStatusByPath[page].published
                    : parsePublishedValue(pageData?.Published);
            const nextPublished = !currentPublished;

            await API.patch(`${company_id}/pages`, {
                page_name: page,
                data: {
                    components_to_update: [
                        {
                            name: 'page',
                            data_file: 'page.json',
                            props: {
                                ...pageData,
                                Published: nextPublished,
                            },
                        },
                    ],
                    components_to_delete: [],
                },
            });

            setPageStatusByPath((prev) => ({
                ...prev,
                [page]: {
                    ...(prev[page] || {}),
                    published: nextPublished,
                    noindex: parseNoIndexValue(pageData?.NoIndex),
                    error: false,
                },
            }));
            setPageJsonByPath((prev) => ({
                ...prev,
                [page]: {
                    ...pageData,
                    Published: nextPublished,
                },
            }));

            await syncSitemapConfigWithTemplate();

            notifications.push({
                title: nextPublished ? 'Page published!' : 'Page unpublished!',
                message: `/${page === 'index' ? '' : page} ${
                    nextPublished ? 'is now published.' : 'is now unpublished.'
                }`,
                variant: 'success',
                ttl: 8,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to update publish status',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setUpdatingPublishedPage(null);
        }
    };

    const handleToggleNoIndexFromMenu = async (selectedPage, event = null) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const page = normalizePageName(selectedPage);
        if (updatingNoIndexPage) return;

        setUpdatingNoIndexPage(page);
        try {
            let pageData = pageJsonByPath[page] || null;
            if (!pageData) {
                const response = await fetch(
                    getStaticJsonPath(page, 'page.json'),
                    {
                        cache: 'no-store',
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Failed to load page data (${response.status}) for ${page}`
                    );
                }

                pageData = await response.json();
            }

            const currentNoIndex =
                typeof pageStatusByPath[page]?.noindex === 'boolean'
                    ? pageStatusByPath[page].noindex
                    : parseNoIndexValue(pageData?.NoIndex);
            const nextNoIndex = !currentNoIndex;

            await API.patch(`${company_id}/pages`, {
                page_name: page,
                data: {
                    components_to_update: [
                        {
                            name: 'page',
                            data_file: 'page.json',
                            props: {
                                ...pageData,
                                NoIndex: nextNoIndex,
                            },
                        },
                    ],
                    components_to_delete: [],
                },
            });

            setPageStatusByPath((prev) => ({
                ...prev,
                [page]: {
                    ...(prev[page] || {}),
                    published: parsePublishedValue(pageData?.Published),
                    noindex: nextNoIndex,
                    error: false,
                },
            }));
            setPageJsonByPath((prev) => ({
                ...prev,
                [page]: {
                    ...pageData,
                    NoIndex: nextNoIndex,
                },
            }));

            notifications.push({
                title: nextNoIndex ? 'NoIndex enabled!' : 'NoIndex disabled!',
                message: `/${page === 'index' ? '' : page} ${
                    nextNoIndex
                        ? 'is now hidden from indexing.'
                        : 'is now indexable.'
                }`,
                variant: 'success',
                ttl: 8,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to update NoIndex status',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setUpdatingNoIndexPage(null);
        }
    };

    const handleSavePageNameFromMenu = async (selectedPage, targetName) => {
        const page = normalizePageName(selectedPage);
        const nextName = String(targetName || '').trim();
        if (!nextName) {
            notifications.push({
                title: 'Invalid page name',
                message: 'Please enter a page name.',
                variant: 'error',
                ttl: 8,
            });
            return false;
        }

        if (updatingPageNamePage) return false;

        setUpdatingPageNamePage(page);
        try {
            await savePageNameValue(page, nextName);

            notifications.push({
                title: 'Page name updated!',
                message: `Updated name for ${pageNameToPath(page)}.`,
                variant: 'success',
                ttl: 8,
            });

            setEditingPageNamePage(null);
            setEditingPageNameInput('');
            return true;
        } catch (error) {
            notifications.push({
                title: 'Failed to update page name',
                variant: 'error',
                ttl: 20,
                error,
            });
            return false;
        } finally {
            setUpdatingPageNamePage(null);
        }
    };

    const renderStatusBadges = (page) => {
        const status = pageStatusByPath[page];

        if (!status) {
            return (
                <span
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-100 text-neutral-500"
                    title="Loading page status"
                >
                    <InformationCircleIcon className="h-3.5 w-3.5" />
                </span>
            );
        }

        return (
            <>
                {status.error ? (
                    <span
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-700"
                        title="currently deploying - reload when completed"
                    >
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    </span>
                ) : null}
                <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-all ${
                        status.published
                            ? 'bg-green-100 text-green-700'
                            : 'bg-neutral-200 text-neutral-600'
                    } ${
                        updatingPublishedPage === page
                            ? 'cursor-not-allowed opacity-70'
                            : 'cursor-pointer hover:brightness-95'
                    }`}
                    title={
                        status.published
                            ? 'Published (click to unpublish)'
                            : 'Unpublished (click to publish)'
                    }
                    onClick={(event) => {
                        void handleTogglePublishedFromMenu(page, event);
                    }}
                >
                    <CheckIcon
                        className={`h-3.5 w-3.5 ${
                            updatingPublishedPage === page ? 'opacity-50' : ''
                        }`}
                    />
                </span>
                <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${
                        status.noindex
                            ? 'bg-neutral-100 text-neutral-400'
                            : 'bg-primary-100 text-primary-700'
                    } transition-all ${
                        updatingNoIndexPage === page
                            ? 'cursor-not-allowed opacity-70'
                            : 'cursor-pointer hover:brightness-95'
                    }`}
                    title={status.noindex ? 'NoIndex' : 'Indexable'}
                    onClick={(event) => {
                        void handleToggleNoIndexFromMenu(page, event);
                    }}
                >
                    {status.noindex ? (
                        <EyeSlashIcon
                            className={`h-3.5 w-3.5 ${
                                updatingNoIndexPage === page ? 'opacity-50' : ''
                            }`}
                        />
                    ) : (
                        <EyeIcon
                            className={`h-3.5 w-3.5 ${
                                updatingNoIndexPage === page ? 'opacity-50' : ''
                            }`}
                        />
                    )}
                </span>
            </>
        );
    };

    const clonePageToTarget = async ({ sourcePage, targetPage }) => {
        await API.post(`${company_id}/pages`, {
            page: getDuplicateTemplateType(sourcePage),
            path: targetPage,
        });

        let sourceManifest = null;
        try {
            const manifestResponse = await fetch(
                `/api/editor-page-manifest?page=${encodeURIComponent(
                    sourcePage
                )}`,
                { cache: 'no-store' }
            );
            if (manifestResponse.ok) {
                const payload = await manifestResponse.json();
                sourceManifest = payload?.manifest || null;
            }
        } catch {
            // continue with newly created template page
        }

        if (sourceManifest && Array.isArray(sourceManifest.components)) {
            const componentEntries = await Promise.all(
                sourceManifest.components.map(async (component) => {
                    const dataFile = `${component.slug}.json`;
                    try {
                        const response = await fetch(
                            getStaticJsonPath(sourcePage, dataFile),
                            {
                                cache: 'no-store',
                            }
                        );
                        if (!response.ok) return null;
                        const props = await response.json();
                        return {
                            name: component.component_key,
                            data_file: dataFile,
                            props,
                        };
                    } catch {
                        return null;
                    }
                })
            );

            const sourcePageResponse = await fetch(
                getStaticJsonPath(sourcePage, 'page.json'),
                { cache: 'no-store' }
            );
            const sourcePageData = sourcePageResponse.ok
                ? await sourcePageResponse.json()
                : null;

            const components_to_update = componentEntries.filter(Boolean);
            if (sourcePageData) {
                components_to_update.push({
                    name: 'page',
                    data_file: 'page.json',
                    props: sourcePageData,
                });
            }

            await API.patch(`${company_id}/pages`, {
                page_name: targetPage,
                data: {
                    manifest: {
                        ...sourceManifest,
                        page_name: targetPage,
                    },
                    components_to_update,
                    components_to_delete: [],
                },
            });
        }
    };

    const syncRenameRedirects = async (renamePairs = []) => {
        if (!company_id || !Array.isArray(renamePairs) || !renamePairs.length) {
            return true;
        }

        const runWithRetry = async (requestFn, attempts = 5) => {
            let lastError = null;
            for (let attempt = 1; attempt <= attempts; attempt += 1) {
                try {
                    return await requestFn();
                } catch (error) {
                    lastError = error;
                    if (attempt < attempts) {
                        await new Promise((resolve) =>
                            window.setTimeout(resolve, 300 * attempt)
                        );
                    }
                }
            }
            throw lastError;
        };

        const sanitized = renamePairs
            .map((pair) => {
                const source = normalizeRoutePath(pair?.source);
                const destination = normalizeRoutePath(pair?.destination);
                return { source, destination };
            })
            .filter(
                ({ source, destination }) =>
                    !!source && !!destination && source !== destination
            );

        if (!sanitized.length) return true;

        try {
            let existing = [];
            try {
                const existingRules = await runWithRetry(
                    () => API.get(`${company_id}/redirects`),
                    5
                );
                existing = Array.isArray(existingRules) ? existingRules : [];
                writeCachedRedirects(company_id, existing);
            } catch (getError) {
                const cachedRedirects = readCachedRedirects(company_id);
                // If redirects don't exist yet (first rename on a fresh site)
                // or the read endpoint is temporarily unavailable, continue
                // with an empty base list so POST can create the file.
                existing = cachedRedirects.length ? cachedRedirects : [];
                if (!cachedRedirects.length) {
                    console.warn(
                        'Redirect list unavailable; creating redirects file from rename payload.',
                        getError
                    );
                }
            }
            const managedSources = new Set(
                sanitized.map((item) => item.source)
            );

            const preserved = existing.filter((rule) => {
                if (!rule || typeof rule !== 'object') return false;
                const source = normalizeRoutePath(rule.source);
                return !managedSources.has(source);
            });

            const nextRules = [
                ...preserved,
                ...sanitized.map(({ source, destination }) => ({
                    source,
                    destination,
                    permanent: true,
                })),
            ];
            writeCachedRedirects(company_id, nextRules);

            await runWithRetry(
                () =>
                    API.post(`${company_id}/redirects`, {
                        redirects: nextRules,
                    }),
                5
            );
            writeCachedRedirects(company_id, nextRules);

            if (typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('editor:redirects-updated', {
                        detail: nextRules,
                    })
                );
            }
            return true;
        } catch (error) {
            console.error('Redirect sync failed after rename:', error);
            return false;
        }
    };

    const handleDuplicatePage = async (selectedPage, targetValue) => {
        const sourcePage = normalizePageName(selectedPage);

        const targetPage = finalizePagePath(targetValue);
        if (!targetPage || targetPage === 'index') {
            notifications.push({
                title: 'Invalid page URL',
                message: 'Please enter a valid page slug/path.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (!isSelectableEditorPage(targetPage)) {
            notifications.push({
                title: 'Invalid target path',
                message:
                    'Path contains unsupported segments. Use letters, numbers, dashes and slashes.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (
            pageList.some((page) => normalizePageName(page) === targetPage) ||
            targetPage === pagename
        ) {
            notifications.push({
                title: 'Page already exists',
                message: `/${targetPage} already exists.`,
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        setDuplicatingPage(sourcePage);
        try {
            await clonePageToTarget({ sourcePage, targetPage });

            setPageList((prev) =>
                toOrderedPageList([...prev, targetPage], pagename)
            );
            if (pageStatusByPath[sourcePage]) {
                setPageStatusByPath((prev) => ({
                    ...prev,
                    [targetPage]: pageStatusByPath[sourcePage],
                }));
            }
            if (pageJsonByPath[sourcePage]) {
                setPageJsonByPath((prev) => ({
                    ...prev,
                    [targetPage]: structuredClone(pageJsonByPath[sourcePage]),
                }));
            }

            notifications.push({
                title: 'Page duplicated!',
                message: `Created /${targetPage}.`,
                variant: 'success',
                ttl: 10,
            });
            setDuplicateEditorPage(null);
            setDuplicateTargetInput('');
        } catch (error) {
            notifications.push({
                title: 'Failed to duplicate page',
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setDuplicatingPage(null);
        }
    };

    const handleRenamePage = async (
        selectedPage,
        targetValue,
        {
            silentSuccess = false,
            syncRedirect = true,
            navigateOnCurrent = true,
            resetEditors = true,
        } = {}
    ) => {
        const sourcePage = normalizePageName(selectedPage);
        if (sourcePage === 'index') {
            notifications.push({
                title: 'Cannot rename home page',
                message: 'Please rename another page.',
                variant: 'error',
                ttl: 8,
            });
            return false;
        }

        const targetPage = finalizePagePath(targetValue);
        if (!targetPage || targetPage === 'index') {
            notifications.push({
                title: 'Invalid page URL',
                message: 'Please enter a valid page slug/path.',
                variant: 'error',
                ttl: 8,
            });
            return false;
        }

        if (!isSelectableEditorPage(targetPage)) {
            notifications.push({
                title: 'Invalid target path',
                message:
                    'Path contains unsupported segments. Use letters, numbers, dashes and slashes.',
                variant: 'error',
                ttl: 8,
            });
            return false;
        }

        if (targetPage === sourcePage) {
            if (resetEditors) {
                setRenameEditorPage(null);
                setRenameTargetInput('');
            }
            return false;
        }

        const pageExists = pageList.some(
            (page) => normalizePageName(page) === targetPage
        );
        if (pageExists || targetPage === pagename) {
            const existingStatus = pageStatusByPath[targetPage];
            if (pageExists && existingStatus?.error) {
                // Clear stale/unknown entry and continue rename attempt.
                setPageList((prev) =>
                    prev.filter((page) => {
                        const normalized = normalizePageName(page);
                        return normalized !== targetPage;
                    })
                );
                setPageStatusByPath((prev) => {
                    const next = { ...prev };
                    delete next[targetPage];
                    return next;
                });
                setPageJsonByPath((prev) => {
                    const next = { ...prev };
                    delete next[targetPage];
                    return next;
                });
            } else {
                notifications.push({
                    title: 'Page already exists',
                    message: `/${targetPage} already exists.`,
                    variant: 'error',
                    ttl: 8,
                });
                return false;
            }
        }

        setRenamingPage(sourcePage);
        try {
            await clonePageToTarget({ sourcePage, targetPage });
            try {
                await API.delete(`${company_id}/pages`, {
                    path: sourcePage,
                });
            } catch (deleteError) {
                // Some deployments report transient delete failures while the
                // target page already exists. In that case we continue and let
                // the follow-up delete settle asynchronously.
                let hasTarget = false;
                let hasSource = true;

                for (let attempt = 0; attempt < 4; attempt += 1) {
                    const latestPages = await fetchEditorApiPages(company_id, {
                        recursive: true,
                    }).catch(() => []);
                    const latestPageSet = new Set(
                        latestPages.map((page) => normalizePageName(page))
                    );
                    hasTarget = latestPageSet.has(targetPage);
                    hasSource = latestPageSet.has(sourcePage);

                    if (hasTarget && !hasSource) break;
                    if (attempt < 3) {
                        await new Promise((resolve) =>
                            window.setTimeout(resolve, 250 * (attempt + 1))
                        );
                    }
                }

                if (!hasTarget) {
                    throw deleteError;
                }

                if (hasSource) {
                    void API.delete(`${company_id}/pages`, {
                        path: sourcePage,
                    }).catch(() => {
                        // best effort; backend usually settles on next commit
                    });
                }
            }

            const currentPageAfterRename =
                sourcePage === pagename ? targetPage : pagename;
            setPageList((prev) =>
                toOrderedPageList(
                    prev.map((page) =>
                        normalizePageName(page) === sourcePage
                            ? targetPage
                            : page
                    ),
                    currentPageAfterRename
                )
            );

            if (pageStatusByPath[sourcePage]) {
                setPageStatusByPath((prev) => {
                    const next = { ...prev };
                    next[targetPage] = next[sourcePage];
                    delete next[sourcePage];
                    return next;
                });
            }
            if (pageJsonByPath[sourcePage]) {
                setPageJsonByPath((prev) => {
                    const next = { ...prev };
                    next[targetPage] = next[sourcePage];
                    delete next[sourcePage];
                    return next;
                });
            }

            if (!silentSuccess) {
                notifications.push({
                    title: 'Page renamed!',
                    message: `Renamed /${sourcePage} to /${targetPage}.`,
                    variant: 'success',
                    ttl: 10,
                });
            }

            if (syncRedirect) {
                await syncRenameRedirects([
                    {
                        source: sourcePage,
                        destination: targetPage,
                    },
                ]);
            }

            if (navigateOnCurrent && sourcePage === pagename) {
                const nextPath = pageNameToPath(targetPage);
                const queryString = searchParams?.toString() || '';
                router.push(
                    queryString ? `${nextPath}?${queryString}` : nextPath
                );
            }

            if (resetEditors) {
                setRenameEditorPage(null);
                setRenameTargetInput('');
            }
            return true;
        } catch (error) {
            notifications.push({
                title: 'Failed to rename page',
                variant: 'error',
                ttl: 20,
                error,
            });
            return false;
        } finally {
            setRenamingPage(null);
        }
    };

    const handleRenameFolder = async (selectedFolder, targetValue) => {
        const sourceFolder = toFolderSegment(selectedFolder);
        const targetFolder = toFolderSegment(targetValue);

        if (!sourceFolder || !targetFolder) {
            notifications.push({
                title: 'Invalid folder path',
                message: 'Please enter a valid folder slug.',
                variant: 'error',
                ttl: 8,
            });
            return false;
        }

        if (sourceFolder === targetFolder) {
            setRenameFolderEditor(null);
            setRenameFolderInput('');
            return false;
        }

        if (targetFolder === 'index' || !isSelectableEditorPage(targetFolder)) {
            notifications.push({
                title: 'Invalid folder path',
                message:
                    'Use only letters, numbers, and dashes for folder names.',
                variant: 'error',
                ttl: 8,
            });
            return false;
        }

        const sourcePrefix = `${sourceFolder}/`;
        const sourcePages = pageList.filter((page) =>
            normalizePageName(page).startsWith(sourcePrefix)
        );
        if (!sourcePages.length) {
            notifications.push({
                title: 'Folder is empty',
                message: 'No pages were found in this folder.',
                variant: 'error',
                ttl: 8,
            });
            return false;
        }

        const sourceSet = new Set(
            sourcePages.map((page) => normalizePageName(page))
        );
        const allPages = new Set(
            pageList.map((page) => normalizePageName(page))
        );
        const renamePairs = sourcePages.map((sourcePage) => {
            const normalizedSource = normalizePageName(sourcePage);
            const relative = normalizedSource.slice(sourcePrefix.length);
            return {
                source: normalizedSource,
                destination: `${targetFolder}/${relative}`,
            };
        });

        const conflicts = renamePairs
            .filter(
                ({ destination }) =>
                    allPages.has(destination) && !sourceSet.has(destination)
            )
            .map(({ destination }) => destination);
        if (conflicts.length) {
            notifications.push({
                title: 'Folder rename conflict',
                message: `/${conflicts[0]} already exists.`,
                variant: 'error',
                ttl: 10,
            });
            return false;
        }

        setRenamingFolder(sourceFolder);
        try {
            for (const pair of renamePairs) {
                const renamed = await handleRenamePage(
                    pair.source,
                    pair.destination,
                    {
                        silentSuccess: true,
                        syncRedirect: false,
                        navigateOnCurrent: false,
                        resetEditors: false,
                    }
                );
                if (!renamed) {
                    throw new Error(
                        `Rename operation aborted for /${pair.source}`
                    );
                }
            }

            await syncRenameRedirects(renamePairs);

            setExpandedFolders((prev) => {
                const next = { ...prev };
                const expanded = next[sourceFolder];
                delete next[sourceFolder];
                next[targetFolder] =
                    typeof expanded === 'boolean' ? expanded : true;
                return next;
            });

            if (pagename.startsWith(sourcePrefix)) {
                const newCurrentPage = pagename.replace(
                    sourcePrefix,
                    `${targetFolder}/`
                );
                const nextPath = pageNameToPath(newCurrentPage);
                const queryString = searchParams?.toString() || '';
                router.push(
                    queryString ? `${nextPath}?${queryString}` : nextPath
                );
            }

            notifications.push({
                title: 'Folder renamed!',
                message: `Renamed /${sourceFolder} to /${targetFolder}.`,
                variant: 'success',
                ttl: 10,
            });

            setRenameFolderEditor(null);
            setRenameFolderInput('');
            return true;
        } catch (error) {
            notifications.push({
                title: 'Failed to rename folder',
                variant: 'error',
                ttl: 20,
                error,
            });
            return false;
        } finally {
            setRenamingFolder(null);
        }
    };

    const openDuplicatePageInlineEditor = (selectedPage) => {
        const sourcePage = normalizePageName(selectedPage);
        if (duplicateEditorPage === sourcePage) {
            setDuplicateEditorPage(null);
            setDuplicateTargetInput('');
            return;
        }

        setDeleteEditorPage(null);
        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setCreatePageFolderName('');
        setCreatePageFolderEditor(null);
        setCreatePageFolderInput('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setRenameEditorPage(null);
        setRenameTargetInput('');
        setDuplicateEditorPage(sourcePage);
        setDuplicateTargetInput(finalizePagePath(`${sourcePage}-copy`));
    };

    const closeDuplicateInlineEditor = () => {
        if (duplicatingPage) return;
        setDuplicateEditorPage(null);
        setDuplicateTargetInput('');
    };

    const submitDuplicateInlineEditor = async (sourcePage) => {
        await handleDuplicatePage(sourcePage, duplicateTargetInput);
    };

    const submitDeleteInlineEditor = async (sourcePage) => {
        await handleConfirmDeletePage({
            targetPageInput: sourcePage,
            targetTypeInput: 'page',
            fromInline: true,
        });
    };

    const openRenamePageInlineEditor = (selectedPage) => {
        const sourcePage = normalizePageName(selectedPage);
        if (sourcePage === 'index') {
            notifications.push({
                title: 'Cannot rename home page',
                message: 'Please rename another page.',
                variant: 'error',
                ttl: 8,
            });
            return;
        }

        if (renameEditorPage === sourcePage) {
            setRenameEditorPage(null);
            setRenameTargetInput('');
            return;
        }

        setDeleteEditorPage(null);
        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setCreatePageFolderName('');
        setCreatePageFolderEditor(null);
        setCreatePageFolderInput('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setDuplicateEditorPage(null);
        setDuplicateTargetInput('');
        setRenameEditorPage(sourcePage);
        setRenameTargetInput(sourcePage);
    };

    const openRenameFolderInlineEditor = (folder) => {
        const sourceFolder = toFolderSegment(folder);
        if (!sourceFolder) return;

        if (renameFolderEditor === sourceFolder) {
            setRenameFolderEditor(null);
            setRenameFolderInput('');
            return;
        }

        setDeleteEditorPage(null);
        setCreatePageFolderEditor(null);
        setCreatePageFolderName('');
        setCreatePageFolderInput('');
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
        setDuplicateEditorPage(null);
        setDuplicateTargetInput('');
        setRenameEditorPage(null);
        setRenameTargetInput('');
        setRenameFolderEditor(sourceFolder);
        setRenameFolderInput(sourceFolder);
        setExpandedFolders((prev) => ({
            ...prev,
            [sourceFolder]: true,
        }));
    };

    const closeRenameFolderInlineEditor = () => {
        if (renamingFolder) return;
        setRenameFolderEditor(null);
        setRenameFolderInput('');
    };

    const submitRenameFolderInlineEditor = async (folder) => {
        await handleRenameFolder(folder, renameFolderInput);
    };

    const closeRenameInlineEditor = () => {
        if (renamingPage) return;
        setRenameEditorPage(null);
        setRenameTargetInput('');
    };

    const openEditPageNameInlineEditor = (selectedPage) => {
        const sourcePage = normalizePageName(selectedPage);

        if (editingPageNamePage === sourcePage) {
            setEditingPageNamePage(null);
            setEditingPageNameInput('');
            return;
        }

        setDeleteEditorPage(null);
        setRenameFolderEditor(null);
        setRenameFolderInput('');
        setCreatePageFolderName('');
        setCreatePageFolderEditor(null);
        setCreatePageFolderInput('');
        setDuplicateEditorPage(null);
        setDuplicateTargetInput('');
        setRenameEditorPage(null);
        setRenameTargetInput('');
        setEditingPageNamePage(sourcePage);
        setEditingPageNameInput(getPageDisplayName(sourcePage, sourcePage));
    };

    const closeEditPageNameInlineEditor = () => {
        if (updatingPageNamePage) return;
        setEditingPageNamePage(null);
        setEditingPageNameInput('');
    };

    const submitEditPageNameInlineEditor = async (page) => {
        await handleSavePageNameFromMenu(page, editingPageNameInput);
    };

    const submitRenameInlineEditor = async (sourcePage) => {
        await handleRenamePage(sourcePage, renameTargetInput);
    };

    const INLINE_ACTION_BUTTON_CLASSNAME =
        'focus-visible:ring-primary-500/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full text-neutral-500 transition-all hover:bg-primary-100 hover:text-primary-700 focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50';
    const INLINE_DANGER_ACTION_BUTTON_CLASSNAME =
        'focus-visible:ring-primary-500/60 inline-flex h-6 w-6 shrink-0 items-center justify-center self-center rounded-full text-neutral-500 transition-all hover:bg-red-100 hover:text-red-600 focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50';

    const renderInlineEditButton = ({
        title,
        onClick,
        iconClassName = 'text-neutral-400',
    }) => (
        <button
            type="button"
            title={title}
            onClick={(event) => {
                event.stopPropagation();
                onClick();
            }}
            className={`${INLINE_ACTION_BUTTON_CLASSNAME} group-focus-visible-within:opacity-100 opacity-0 group-hover:opacity-100`}
        >
            <PencilSquareIcon className={`h-3.5 w-3.5 ${iconClassName}`} />
        </button>
    );

    const renderInlineCommitButtons = ({
        isSaving,
        saveTitle,
        cancelTitle,
        onCancel,
    }) => (
        <>
            <button
                type="submit"
                title={saveTitle}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSaving}
            >
                <CheckIcon className="h-3 w-3" />
            </button>
            <button
                type="button"
                title={cancelTitle}
                onClick={onCancel}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-all hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSaving}
            >
                <XMarkIcon className="h-3 w-3" />
            </button>
        </>
    );

    const renderPageNameInlineField = (
        page,
        fallbackLabel = page,
        onNavigate,
        icon
    ) => {
        if (editingPageNamePage !== page) {
            return (
                <div className="group flex min-w-0 items-center gap-1">
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onNavigate();
                        }}
                        className="focus-visible:ring-primary-500/60 flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-0.5 text-left text-neutral-800 transition-all hover:text-primary-700 focus:outline-none focus-visible:ring-2"
                        title={`Open ${pageNameToPath(page)}`}
                    >
                        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-neutral-400">
                            {icon}
                        </span>
                        <span className="truncate">
                            {getPageDisplayName(page, fallbackLabel)}
                        </span>
                    </button>
                    {renderInlineEditButton({
                        title: 'Edit Page Name',
                        onClick: () => openEditPageNameInlineEditor(page),
                    })}
                </div>
            );
        }

        return (
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-neutral-400">
                    {icon}
                </span>
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        void submitEditPageNameInlineEditor(page);
                    }}
                    onClick={(event) => event.stopPropagation()}
                    className="flex min-w-0 flex-1 items-center gap-1.5"
                >
                    <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <input
                            type="text"
                            value={editingPageNameInput}
                            onChange={(event) =>
                                setEditingPageNameInput(event.target.value)
                            }
                            placeholder="Page name"
                            className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                            disabled={!!updatingPageNamePage}
                            autoFocus
                        />
                    </div>
                    {renderInlineCommitButtons({
                        isSaving: updatingPageNamePage === page,
                        saveTitle: 'Save Page Name',
                        cancelTitle: 'Cancel Page Name Edit',
                        onCancel: closeEditPageNameInlineEditor,
                    })}
                </form>
            </div>
        );
    };

    const renderPageSlugInlineField = (page, onNavigate) => {
        if (page === 'index') {
            return (
                <span className="inline-flex min-w-0 items-center rounded-md px-1 py-0.5 text-neutral-500">
                    <span className="truncate">{pageNameToPath(page)}</span>
                </span>
            );
        }

        if (renameEditorPage !== page) {
            return (
                <div className="group flex min-w-0 items-center gap-1">
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onNavigate();
                        }}
                        className="focus-visible:ring-primary-500/60 min-w-0 rounded-md px-1 py-0.5 text-left text-primary-600 transition-all hover:text-primary-700 hover:underline focus:outline-none focus-visible:ring-2"
                        title={`Open ${pageNameToPath(page)}`}
                    >
                        <span className="block truncate">
                            {pageNameToPath(page)}
                        </span>
                    </button>
                    {renderInlineEditButton({
                        title: 'Edit Page URL',
                        onClick: () => openRenamePageInlineEditor(page),
                        iconClassName: 'text-primary-400',
                    })}
                </div>
            );
        }

        const sourcePage = normalizePageName(page);
        const nextPage = finalizePagePath(renameTargetInput);
        const showRedirectPreview =
            !!nextPage &&
            nextPage !== 'index' &&
            nextPage !== sourcePage &&
            isSelectableEditorPage(nextPage);

        return (
            <div className="min-w-0 flex-1">
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        void submitRenameInlineEditor(page);
                    }}
                    onClick={(event) => event.stopPropagation()}
                    className="flex min-w-0 items-center gap-1.5"
                >
                    <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <input
                            type="text"
                            value={renameTargetInput}
                            onChange={(event) =>
                                setRenameTargetInput(event.target.value)
                            }
                            placeholder="service/new-slug"
                            className="block h-6 w-full rounded-[10px] border-0 bg-white px-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                            disabled={!!renamingPage}
                            autoFocus
                        />
                    </div>
                    {renderInlineCommitButtons({
                        isSaving: renamingPage === page,
                        saveTitle: 'Save Page URL',
                        cancelTitle: 'Cancel Page URL Edit',
                        onCancel: closeRenameInlineEditor,
                    })}
                </form>
                {showRedirectPreview ? (
                    <p className="mt-1 px-1 text-[11px] text-neutral-500">
                        A redirect will be added: {pageNameToPath(sourcePage)}{' '}
                        to {pageNameToPath(nextPage)}
                    </p>
                ) : null}
            </div>
        );
    };

    const renderDuplicateInlineForm = (page) => {
        if (duplicateEditorPage !== page) return null;

        return (
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    submitDuplicateInlineEditor(page);
                }}
                className="mt-2 border-t border-[#9052F1]/20 pt-2"
            >
                <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <input
                            type="text"
                            value={duplicateTargetInput}
                            onChange={(event) =>
                                setDuplicateTargetInput(event.target.value)
                            }
                            placeholder="service/new-page"
                            className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                            disabled={!!duplicatingPage}
                        />
                    </div>
                    <button
                        type="submit"
                        className="inline-flex items-center rounded-full bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={duplicatingPage === page}
                    >
                        {duplicatingPage === page
                            ? 'Duplicating...'
                            : 'Duplicate'}
                    </button>
                    <button
                        type="button"
                        onClick={closeDuplicateInlineEditor}
                        className="inline-flex items-center rounded-full border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-all hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!!duplicatingPage}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        );
    };

    const renderDeleteInlineForm = (page) => {
        if (deleteEditorPage !== page) return null;
        const displayName = getPageDisplayName(page, page);

        return (
            <div className="mt-2 border-t border-red-200 pt-2">
                <p className="text-xs text-neutral-600">
                    Delete <strong>{displayName}</strong>? This action cannot be
                    undone.
                </p>
                <div className="mt-2 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            void submitDeleteInlineEditor(page);
                        }}
                        className="inline-flex items-center rounded-full bg-red-600 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={isDeletingTargetPage}
                    >
                        {isDeletingTargetPage ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                        type="button"
                        onClick={closeDeletePageInlineEditor}
                        className="inline-flex items-center rounded-full border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-all hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={isDeletingTargetPage}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    const renderRenameFolderInlineForm = (folder) => {
        const sourceFolder = toFolderSegment(folder);
        if (!sourceFolder || renameFolderEditor !== sourceFolder) return null;

        return (
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    submitRenameFolderInlineEditor(sourceFolder);
                }}
                className="border-t border-primary-200 bg-white px-3 py-2"
            >
                <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <input
                            type="text"
                            value={renameFolderInput}
                            onChange={(event) =>
                                setRenameFolderInput(event.target.value)
                            }
                            placeholder="services"
                            className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                            disabled={!!renamingFolder}
                        />
                    </div>
                    <button
                        type="submit"
                        className="inline-flex items-center rounded-full bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={renamingFolder === sourceFolder}
                    >
                        {renamingFolder === sourceFolder
                            ? 'Renaming...'
                            : 'Rename'}
                    </button>
                    <button
                        type="button"
                        onClick={closeRenameFolderInlineEditor}
                        className="inline-flex items-center rounded-full border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-all hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!!renamingFolder}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        );
    };

    const renderCreatePageInFolderInlineForm = (folder) => {
        const folderSegment = toFolderSegment(folder);
        if (!folderSegment || createPageFolderEditor !== folderSegment)
            return null;

        const selectedTemplateLabel =
            NEW_PAGE_TEMPLATE_OPTIONS.find(
                (option) => option.value === newPageTemplate
            )?.label || NEW_PAGE_TEMPLATE_OPTIONS[0].label;

        return (
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    void handleCreatePageInFolderFromMenu(folderSegment);
                }}
                className="space-y-2 border-t border-primary-200 bg-white px-3 py-2"
            >
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                    Create Page In /{folderSegment}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="min-w-0 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <input
                            type="text"
                            value={createPageFolderName}
                            onChange={(event) =>
                                setCreatePageFolderName(event.target.value)
                            }
                            placeholder="Page name"
                            className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                            disabled={isCreatingPage}
                        />
                    </div>
                    <div className="min-w-0 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <input
                            type="text"
                            value={createPageFolderInput}
                            onChange={(event) =>
                                setCreatePageFolderInput(
                                    sanitizePagePathInput(event.target.value)
                                )
                            }
                            placeholder="Page URL (e.g. test)"
                            className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                            disabled={isCreatingPage}
                        />
                    </div>
                </div>
                <div className="space-y-1.5 pt-0.5 sm:w-[calc((100%-0.5rem)/2)]">
                    <p className="text-xs font-semibold text-neutral-600">
                        Choose template
                    </p>
                    <div className="relative rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                        <Listbox
                            value={newPageTemplate}
                            onChange={setNewPageTemplate}
                            disabled={isCreatingPage}
                        >
                            <div className="relative">
                                <Listbox.Button
                                    className="relative flex h-6 w-full items-center rounded-[10px] bg-white px-3 pr-7 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                                    aria-label="Template"
                                >
                                    <span className="truncate">
                                        {selectedTemplateLabel}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                        <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                                    </span>
                                </Listbox.Button>
                                <Transition
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Listbox.Options className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        {NEW_PAGE_TEMPLATE_OPTIONS.map(
                                            (option) => (
                                                <Listbox.Option
                                                    key={option.value}
                                                    value={option.value}
                                                    className={({
                                                        active,
                                                        selected,
                                                    }) =>
                                                        [
                                                            'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                            active
                                                                ? 'bg-primary-50 text-primary-700'
                                                                : 'text-neutral-800',
                                                            selected
                                                                ? 'font-medium'
                                                                : '',
                                                        ].join(' ')
                                                    }
                                                >
                                                    {({ selected }) => (
                                                        <>
                                                            <span className="truncate">
                                                                {option.label}
                                                            </span>
                                                            {selected && (
                                                                <CheckIcon className="h-4 w-4 text-primary-600" />
                                                            )}
                                                        </>
                                                    )}
                                                </Listbox.Option>
                                            )
                                        )}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="submit"
                        className="inline-flex items-center rounded-full bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={isCreatingPage}
                    >
                        {isCreatingPage ? 'Creating...' : 'Create Page'}
                    </button>
                    <button
                        type="button"
                        onClick={closeCreatePageInFolderInlineEditor}
                        className="inline-flex items-center rounded-full border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-all hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={isCreatingPage}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        );
    };

    const selectedNewFolderTemplateLabel =
        NEW_PAGE_TEMPLATE_OPTIONS.find(
            (option) => option.value === newFolderTemplate
        )?.label || NEW_PAGE_TEMPLATE_OPTIONS[0].label;

    const selectedNewPageTemplateLabel =
        NEW_PAGE_TEMPLATE_OPTIONS.find(
            (option) => option.value === newPageTemplate
        )?.label || NEW_PAGE_TEMPLATE_OPTIONS[0].label;

    return (
        <>
            {/* Top nav / toolbar */}
            <Disclosure
                as="nav"
                className="fixed z-[10000] w-full bg-neutral-800 shadow-sm"
            >
                <div className="relative mx-auto flex h-12 items-center justify-between px-4">
                    {/* Left: logo + page menu + update buttons */}
                    <div className="flex items-center gap-2">
                        <a
                            href="https://app.nicejob.com/setup/sites/domain-and-call-tracking"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center rounded px-1 outline-none ring-0 hover:opacity-90 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                            title="Open NiceJob Domain & Call Tracking (new tab)"
                            aria-label="Open NiceJob Domain & Call Tracking in a new tab"
                        >
                            <img
                                src={NICEJOB_LOGO}
                                alt="NiceJob"
                                className="h-6 w-auto"
                                draggable="false"
                            />
                        </a>
                        <button
                            type="button"
                            title="Open Site Pages"
                            onClick={() =>
                                setIsPageMenuOpen((isOpen) => !isOpen)
                            }
                            className={`rounded-full p-1.5 transition-all ${
                                isPageMenuOpen
                                    ? 'bg-white/30 text-white'
                                    : 'bg-white/20 text-white hover:bg-[#8F52F1] hover:text-white'
                            }`}
                        >
                            <DocumentTextIcon className="h-4 w-4" />
                        </button>

                        <UpdateEditor
                            company_id={company_id}
                            className="ml-3"
                        />
                        <UpdateComponentsButton company_id={company_id} />
                    </div>

                    {/* Center: breakpoint icons (desktop / tablet / mobile) */}
                    <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-2 md:flex">
                        <BreakpointButton
                            icon={<ComputerDesktopIcon className="h-4 w-4" />}
                            title="Desktop"
                            isActive={activeBreakpoint === 'desktop'}
                            onClick={() => onBreakpointClick('desktop')}
                        />
                        <BreakpointButton
                            icon={<DeviceTabletIcon className="h-4 w-4" />}
                            title="Tablet"
                            isActive={activeBreakpoint === 'tablet'}
                            onClick={() => onBreakpointClick('tablet')}
                        />
                        <BreakpointButton
                            icon={<DevicePhoneMobileIcon className="h-4 w-4" />}
                            title="Mobile"
                            isActive={activeBreakpoint === 'mobile'}
                            onClick={() => onBreakpointClick('mobile')}
                        />
                    </div>

                    {/* Right: actions */}
                    <div className="flex items-center gap-2">
                        <NotificationPanel className="max-w-[24vw] self-center" />

                        {/* Revert / Save */}
                        <div
                            className={`flex items-center gap-2 border-r border-white/30 pr-2 ${
                                editor.has_updates ? 'visible' : 'invisible'
                            }`}
                        >
                            <button
                                type="button"
                                className="rounded-full bg-white/20 p-1.5 text-white transition-all hover:bg-red-500"
                                onClick={editor.revert}
                                title="Revert changes"
                            >
                                <ArrowUturnLeftIcon className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white transition-all hover:bg-[#2CE080]"
                                onClick={editor.save}
                                title="Save changes"
                            >
                                {editor.is_saving ? (
                                    <svg
                                        aria-hidden="true"
                                        className="inline h-4 w-4 animate-spin fill-white text-white/40"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        />
                                    </svg>
                                ) : (
                                    <CloudArrowUpIcon className="h-4 w-4" />
                                )}
                            </button>
                        </div>

                        {/* Edit page settings */}
                        <button
                            type="button"
                            className="rounded-full bg-white/20 p-1.5 text-white transition-all hover:bg-[#2CE080]"
                            title="Edit Page Settings"
                            onClick={() => {
                                setIsPageMenuOpen(false);
                                editor.page.visible({ mode: 'settings' })
                                    ? editor.page.close()
                                    : editor.page.open({ mode: 'settings' });
                            }}
                        >
                            <AdjustmentsHorizontalIcon className="h-4 w-4" />
                        </button>

                        {/* Edit site settings */}
                        <button
                            type="button"
                            className="rounded-full bg-white/20 p-1.5 text-white transition-all hover:bg-primary-600"
                            title="Edit Site Settings"
                            onClick={() => {
                                setIsPageMenuOpen(false);
                                editor.site.visible()
                                    ? editor.site.close()
                                    : editor.site.open();
                            }}
                        >
                            <Cog6ToothIconSolid className="h-4 w-4" />
                        </button>

                        {/* Publish */}
                        <div className="ml-1 flex items-center pl-2">
                            <Publish
                                asButton
                                buttonClass="rounded-full bg-[#2CE080] p-1.5 text-white transition-all hover:bg-[#2CE080]"
                            />
                        </div>

                        {/* Page Component Editor Drawer */}
                        <ComponentEditor
                            file="page.json"
                            componentName="page"
                            component_props={editor.page.data}
                            open={editor.page.visible({ mode: 'settings' })}
                            onSave={editor.save}
                            onChange={editor.page.change}
                            onCancel={() => {
                                editor.page.cancel();
                                editor.page.close();
                            }}
                            onClose={editor.page.close}
                        />
                    </div>
                </div>
            </Disclosure>

            <EditorDrawer
                storageKey="globalEditorDrawerv2"
                title="Site Pages"
                open={isPageMenuOpen}
                onClose={() => setIsPageMenuOpen(false)}
                hideFooter
            >
                <div className="space-y-3 py-4">
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                void savePageOrderToRepository(pageList);
                            }}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                                isPageOrderDirty
                                    ? 'bg-primary-600 text-white hover:bg-primary-500'
                                    : 'bg-neutral-200 text-neutral-600'
                            }`}
                            disabled={!isPageOrderDirty || isSavingPageOrder}
                            title={
                                isPageOrderDirty
                                    ? 'Save page order'
                                    : 'Page order saved'
                            }
                        >
                            <CloudArrowUpIcon className="h-3.5 w-3.5" />
                            {isSavingPageOrder ? 'Saving...' : 'Save Order'}
                        </button>
                        <button
                            type="button"
                            onClick={openNewFolderFromMenu}
                            className="inline-flex items-center gap-1.5 rounded-full bg-secondary-700 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-secondary-600"
                        >
                            <FolderPlusIcon className="h-3.5 w-3.5" />
                            New Folder
                        </button>
                        <button
                            type="button"
                            onClick={openAddPageFromMenu}
                            className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-primary-500"
                        >
                            <PlusIcon className="h-3.5 w-3.5" />
                            New Page
                        </button>
                    </div>

                    {isNewFolderOpen ? (
                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                                handleCreateFolderFromMenu();
                            }}
                            className="space-y-2 rounded-xl border border-secondary-200 bg-white p-3"
                        >
                            <p className="text-xs font-semibold uppercase tracking-wide text-secondary-700">
                                Create Folder
                            </p>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div className="min-w-0 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <input
                                        type="text"
                                        value={newFolderName}
                                        onChange={(event) =>
                                            setNewFolderName(event.target.value)
                                        }
                                        placeholder="Folder (e.g. service)"
                                        className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                        disabled={isCreatingFolder}
                                    />
                                </div>
                                <div className="min-w-0 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <input
                                        type="text"
                                        value={newFolderFirstPage}
                                        onChange={(event) =>
                                            setNewFolderFirstPage(
                                                event.target.value
                                            )
                                        }
                                        placeholder="First page URL (e.g. test)"
                                        className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                        disabled={isCreatingFolder}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5 pt-0.5 sm:w-[calc((100%-0.5rem)/2)]">
                                <p className="text-xs font-semibold text-neutral-600">
                                    Choose template
                                </p>
                                <div className="relative rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <Listbox
                                        value={newFolderTemplate}
                                        onChange={setNewFolderTemplate}
                                        disabled={isCreatingFolder}
                                    >
                                        <div className="relative">
                                            <Listbox.Button
                                                className="relative flex h-6 w-full items-center rounded-[10px] bg-white px-3 pr-7 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                                                aria-label="Folder template"
                                            >
                                                <span className="truncate">
                                                    {
                                                        selectedNewFolderTemplateLabel
                                                    }
                                                </span>
                                                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                                    <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                                                </span>
                                            </Listbox.Button>
                                            <Transition
                                                as={Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100 scale-100"
                                                leaveTo="opacity-0 scale-95"
                                            >
                                                <Listbox.Options className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                                    {NEW_PAGE_TEMPLATE_OPTIONS.map(
                                                        (option) => (
                                                            <Listbox.Option
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                                className={({
                                                                    active,
                                                                    selected,
                                                                }) =>
                                                                    [
                                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                                        active
                                                                            ? 'bg-primary-50 text-primary-700'
                                                                            : 'text-neutral-800',
                                                                        selected
                                                                            ? 'font-medium'
                                                                            : '',
                                                                    ].join(' ')
                                                                }
                                                            >
                                                                {({
                                                                    selected,
                                                                }) => (
                                                                    <>
                                                                        <span className="truncate">
                                                                            {
                                                                                option.label
                                                                            }
                                                                        </span>
                                                                        {selected && (
                                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Listbox.Option>
                                                        )
                                                    )}
                                                </Listbox.Options>
                                            </Transition>
                                        </div>
                                    </Listbox>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="submit"
                                    className="inline-flex items-center rounded-full bg-secondary-700 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={isCreatingFolder}
                                >
                                    {isCreatingFolder
                                        ? 'Creating...'
                                        : 'Create Folder'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeNewFolderFromMenu}
                                    className="inline-flex items-center rounded-full border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-all hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={isCreatingFolder}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    ) : null}

                    {isNewPageOpen ? (
                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                                handleCreatePageFromMenu();
                            }}
                            className="space-y-2 rounded-xl border border-primary-200 bg-white p-3"
                        >
                            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                Create Page
                            </p>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div className="min-w-0 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <input
                                        type="text"
                                        value={newPageName}
                                        onChange={(event) =>
                                            setNewPageName(event.target.value)
                                        }
                                        placeholder="Page name (e.g. Service)"
                                        className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                        disabled={isCreatingPage}
                                    />
                                </div>
                                <div className="min-w-0 rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <input
                                        type="text"
                                        value={newPagePath}
                                        onChange={(event) =>
                                            setNewPagePath(
                                                sanitizePagePathInput(
                                                    event.target.value
                                                )
                                            )
                                        }
                                        placeholder="Page URL (e.g. service/test)"
                                        className="block h-6 w-full rounded-[10px] border-0 bg-white px-3 text-xs leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                        disabled={isCreatingPage}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5 pt-0.5 sm:w-[calc((100%-0.5rem)/2)]">
                                <p className="text-xs font-semibold text-neutral-600">
                                    Choose template
                                </p>
                                <div className="relative rounded-xl bg-neutral-300 p-[1px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                    <Listbox
                                        value={newPageTemplate}
                                        onChange={setNewPageTemplate}
                                        disabled={isCreatingPage}
                                    >
                                        <div className="relative">
                                            <Listbox.Button
                                                className="relative flex h-6 w-full items-center rounded-[10px] bg-white px-3 pr-7 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                                                aria-label="Template"
                                            >
                                                <span className="truncate">
                                                    {
                                                        selectedNewPageTemplateLabel
                                                    }
                                                </span>
                                                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                                                    <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                                                </span>
                                            </Listbox.Button>
                                            <Transition
                                                as={Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100 scale-100"
                                                leaveTo="opacity-0 scale-95"
                                            >
                                                <Listbox.Options className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                                    {NEW_PAGE_TEMPLATE_OPTIONS.map(
                                                        (option) => (
                                                            <Listbox.Option
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                                className={({
                                                                    active,
                                                                    selected,
                                                                }) =>
                                                                    [
                                                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                                        active
                                                                            ? 'bg-primary-50 text-primary-700'
                                                                            : 'text-neutral-800',
                                                                        selected
                                                                            ? 'font-medium'
                                                                            : '',
                                                                    ].join(' ')
                                                                }
                                                            >
                                                                {({
                                                                    selected,
                                                                }) => (
                                                                    <>
                                                                        <span className="truncate">
                                                                            {
                                                                                option.label
                                                                            }
                                                                        </span>
                                                                        {selected && (
                                                                            <CheckIcon className="h-4 w-4 text-primary-600" />
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Listbox.Option>
                                                        )
                                                    )}
                                                </Listbox.Options>
                                            </Transition>
                                        </div>
                                    </Listbox>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="submit"
                                    className="inline-flex items-center rounded-full bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white transition-all hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={isCreatingPage}
                                >
                                    {isCreatingPage
                                        ? 'Creating...'
                                        : 'Create Page'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeNewPageFromMenu}
                                    className="inline-flex items-center rounded-full border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-all hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={isCreatingPage}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    ) : null}

                    {isLoadingPageList ? (
                        <div className="flex justify-center py-2">
                            <div
                                className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600"
                                aria-label="Loading pages"
                                role="status"
                            />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {pageMenuTree.rootPages.map((page) => {
                                const isCurrent = page === pagename;
                                const isDuplicateEditorOpen =
                                    duplicateEditorPage === page;
                                const isRenameEditorOpen =
                                    renameEditorPage === page;
                                const isDeleteEditorOpen =
                                    deleteEditorPage === page;
                                const isBeingDragged = draggingPage === page;
                                return (
                                    <Fragment key={page}>
                                        {renderDropGap(`page:${page}`, page)}
                                        <div
                                            draggable={page !== 'index'}
                                            onDragStart={(event) =>
                                                handlePageDragStart(page, event)
                                            }
                                            onDragEnd={clearDragState}
                                            onDragOver={(event) =>
                                                handleDragOverTarget(
                                                    `page:${page}`,
                                                    event
                                                )
                                            }
                                            onDrop={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                const sourcePage =
                                                    readDraggedPageFromEvent(
                                                        event
                                                    );
                                                if (!sourcePage) {
                                                    clearDragState();
                                                    return;
                                                }
                                                const targetRect =
                                                    event.currentTarget.getBoundingClientRect();
                                                const insertAfter =
                                                    event.clientY >
                                                    targetRect.top +
                                                        targetRect.height / 2;
                                                void handleDropPageToTarget({
                                                    sourcePage,
                                                    targetPage: page,
                                                    insertAfter,
                                                });
                                            }}
                                            className={`w-full rounded-xl border px-3 py-2 text-left transition-all ${
                                                isDuplicateEditorOpen ||
                                                isRenameEditorOpen ||
                                                isDeleteEditorOpen
                                                    ? 'border-[#9052F1]/40 bg-[#9052F1]/5'
                                                    : ''
                                            } ${
                                                dragOverTarget ===
                                                `page:${page}`
                                                    ? 'bg-primary-50/70 border-primary-400'
                                                    : ''
                                            } ${
                                                isCurrent
                                                    ? 'border-primary-300 bg-primary-50'
                                                    : 'hover:bg-primary-50/40 border-neutral-200 bg-white hover:border-primary-200'
                                            } ${
                                                page !== 'index'
                                                    ? 'cursor-grab active:cursor-grabbing'
                                                    : 'cursor-default'
                                            } ${
                                                isBeingDragged
                                                    ? 'opacity-35'
                                                    : ''
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                                                    <div className="min-w-0 space-y-0.5">
                                                        <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-800">
                                                            <div className="min-w-0 flex-1">
                                                                {renderPageNameInlineField(
                                                                    page,
                                                                    page,
                                                                    () =>
                                                                        void handleRootPageSelect(
                                                                            page
                                                                        ),
                                                                    page ===
                                                                        'index' ? (
                                                                        <HomeIcon
                                                                            className="h-4 w-4 shrink-0"
                                                                            aria-hidden="true"
                                                                        />
                                                                    ) : (
                                                                        <DocumentTextIcon
                                                                            className="h-4 w-4 shrink-0"
                                                                            aria-hidden="true"
                                                                        />
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-xs text-primary-600">
                                                            <span
                                                                className={`inline-flex h-5 w-5 shrink-0 items-center justify-center ${
                                                                    page ===
                                                                    'index'
                                                                        ? 'text-neutral-300'
                                                                        : 'text-primary-600'
                                                                }`}
                                                            >
                                                                <LinkIcon className="h-3.5 w-3.5 shrink-0" />
                                                            </span>
                                                            <div className="min-w-0 flex-1">
                                                                {renderPageSlugInlineField(
                                                                    page,
                                                                    () =>
                                                                        void handleRootPageSelect(
                                                                            page
                                                                        )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-0.5 flex items-center gap-1">
                                                        {renderStatusBadges(
                                                            page
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="ml-2 flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        title="Edit Page Settings"
                                                        onClick={() =>
                                                            openPageSettingsFromMenu(
                                                                page
                                                            )
                                                        }
                                                        className={
                                                            INLINE_ACTION_BUTTON_CLASSNAME
                                                        }
                                                    >
                                                        <Cog6ToothIconOutline className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Duplicate Page"
                                                        onClick={() =>
                                                            openDuplicatePageInlineEditor(
                                                                page
                                                            )
                                                        }
                                                        className={
                                                            INLINE_ACTION_BUTTON_CLASSNAME
                                                        }
                                                        disabled={
                                                            !!duplicatingPage
                                                        }
                                                    >
                                                        <DocumentDuplicateIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title={
                                                            page === 'index'
                                                                ? 'Home page cannot be deleted'
                                                                : 'Delete Page'
                                                        }
                                                        onClick={() =>
                                                            openDeleteTargetPageFromMenu(
                                                                page
                                                            )
                                                        }
                                                        className={`${INLINE_DANGER_ACTION_BUTTON_CLASSNAME} ${
                                                            page === 'index'
                                                                ? 'text-neutral-300 hover:bg-transparent hover:text-neutral-300'
                                                                : ''
                                                        }`}
                                                        disabled={
                                                            page === 'index'
                                                        }
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            {renderDuplicateInlineForm(page)}
                                            {renderDeleteInlineForm(page)}
                                        </div>
                                    </Fragment>
                                );
                            })}
                            {renderTrailingDropZone('root:end')}

                            {pageMenuTree.folders.map(
                                ({ folder, children }) => {
                                    const isExpanded =
                                        expandedFolders[folder] ?? false;
                                    const folderHasCurrent = children.some(
                                        (child) => child.path === pagename
                                    );

                                    return (
                                        <div
                                            key={folder}
                                            className="overflow-hidden rounded-xl border border-neutral-200 bg-white"
                                        >
                                            <div
                                                onDragOver={(event) =>
                                                    handleDragOverTarget(
                                                        `folder:${folder}`,
                                                        event
                                                    )
                                                }
                                                onDrop={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    const sourcePage =
                                                        readDraggedPageFromEvent(
                                                            event
                                                        );
                                                    if (!sourcePage) {
                                                        clearDragState();
                                                        return;
                                                    }
                                                    void handleDropPageToTarget(
                                                        {
                                                            sourcePage,
                                                            targetFolder:
                                                                folder,
                                                        }
                                                    );
                                                }}
                                                className={`flex w-full items-center justify-between px-3 py-2 transition-all ${
                                                    dragOverTarget ===
                                                    `folder:${folder}`
                                                        ? 'bg-primary-100'
                                                        : ''
                                                } ${
                                                    folderHasCurrent
                                                        ? 'bg-primary-50'
                                                        : 'hover:bg-primary-50/40'
                                                }`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toggleFolder(folder)
                                                    }
                                                    className="flex min-w-0 flex-1 items-center text-left"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="flex items-center gap-1.5 text-sm font-semibold text-neutral-800">
                                                            <FolderIcon
                                                                className="h-4 w-4 shrink-0 text-neutral-400"
                                                                aria-hidden="true"
                                                            />
                                                            <span>
                                                                {formatPageName(
                                                                    folder
                                                                )}
                                                            </span>
                                                        </p>
                                                        <p className="text-xs text-neutral-500">
                                                            {children.length}{' '}
                                                            page
                                                            {children.length > 1
                                                                ? 's'
                                                                : ''}
                                                        </p>
                                                    </div>
                                                </button>
                                                <div className="ml-2 flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        title="Add Page To Folder"
                                                        onClick={() =>
                                                            openAddPageInFolderFromMenu(
                                                                folder
                                                            )
                                                        }
                                                        className={
                                                            INLINE_ACTION_BUTTON_CLASSNAME
                                                        }
                                                        disabled={
                                                            !!isCreatingPage
                                                        }
                                                    >
                                                        <DocumentPlusIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Rename Folder"
                                                        onClick={() =>
                                                            openRenameFolderInlineEditor(
                                                                folder
                                                            )
                                                        }
                                                        className={
                                                            INLINE_ACTION_BUTTON_CLASSNAME
                                                        }
                                                        disabled={
                                                            !!renamingFolder
                                                        }
                                                    >
                                                        <PencilSquareIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Delete Folder"
                                                        onClick={() =>
                                                            openDeleteTargetFolderFromMenu(
                                                                folder
                                                            )
                                                        }
                                                        className={
                                                            INLINE_DANGER_ACTION_BUTTON_CLASSNAME
                                                        }
                                                        disabled={
                                                            !!isDeletingTargetPage
                                                        }
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            toggleFolder(folder)
                                                        }
                                                        className={
                                                            INLINE_ACTION_BUTTON_CLASSNAME
                                                        }
                                                        title={
                                                            isExpanded
                                                                ? 'Collapse folder'
                                                                : 'Expand folder'
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronDownIcon className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronRightIcon className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            {renderCreatePageInFolderInlineForm(
                                                folder
                                            )}
                                            {renderRenameFolderInlineForm(
                                                folder
                                            )}

                                            {isExpanded ? (
                                                <div className="space-y-1 border-t border-neutral-100 bg-neutral-50/50 p-2">
                                                    {children.map((child) => {
                                                        const isCurrent =
                                                            child.path ===
                                                            pagename;
                                                        const isDuplicateEditorOpen =
                                                            duplicateEditorPage ===
                                                            child.path;
                                                        const isRenameEditorOpen =
                                                            renameEditorPage ===
                                                            child.path;
                                                        const isDeleteEditorOpen =
                                                            deleteEditorPage ===
                                                            child.path;
                                                        const isBeingDragged =
                                                            draggingPage ===
                                                            child.path;

                                                        return (
                                                            <Fragment
                                                                key={child.path}
                                                            >
                                                                {renderDropGap(
                                                                    `page:${child.path}`,
                                                                    child.path
                                                                )}
                                                                <div
                                                                    draggable
                                                                    onDragStart={(
                                                                        event
                                                                    ) =>
                                                                        handlePageDragStart(
                                                                            child.path,
                                                                            event
                                                                        )
                                                                    }
                                                                    onDragEnd={
                                                                        clearDragState
                                                                    }
                                                                    onDragOver={(
                                                                        event
                                                                    ) =>
                                                                        handleDragOverTarget(
                                                                            `page:${child.path}`,
                                                                            event
                                                                        )
                                                                    }
                                                                    onDrop={(
                                                                        event
                                                                    ) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        const sourcePage =
                                                                            readDraggedPageFromEvent(
                                                                                event
                                                                            );
                                                                        if (
                                                                            !sourcePage
                                                                        ) {
                                                                            clearDragState();
                                                                            return;
                                                                        }
                                                                        const targetRect =
                                                                            event.currentTarget.getBoundingClientRect();
                                                                        const insertAfter =
                                                                            event.clientY >
                                                                            targetRect.top +
                                                                                targetRect.height /
                                                                                    2;
                                                                        void handleDropPageToTarget(
                                                                            {
                                                                                sourcePage,
                                                                                targetPage:
                                                                                    child.path,
                                                                                insertAfter,
                                                                            }
                                                                        );
                                                                    }}
                                                                    className={`w-full rounded-lg border px-3 py-2 text-left transition-all ${
                                                                        isDuplicateEditorOpen ||
                                                                        isRenameEditorOpen ||
                                                                        isDeleteEditorOpen
                                                                            ? 'border-[#9052F1]/40 bg-[#9052F1]/5'
                                                                            : ''
                                                                    } ${
                                                                        dragOverTarget ===
                                                                        `page:${child.path}`
                                                                            ? 'bg-primary-50/70 border-primary-400'
                                                                            : ''
                                                                    } ${
                                                                        isCurrent
                                                                            ? 'border-primary-300 bg-primary-50'
                                                                            : 'hover:bg-primary-50/40 border-neutral-200 bg-white hover:border-primary-200'
                                                                    } cursor-grab active:cursor-grabbing ${
                                                                        isBeingDragged
                                                                            ? 'opacity-35'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                                                                            <div className="min-w-0 space-y-0.5">
                                                                                <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-800">
                                                                                    <div className="min-w-0 flex-1">
                                                                                        {renderPageNameInlineField(
                                                                                            child.path,
                                                                                            child.relativePath,
                                                                                            () =>
                                                                                                handlePageSelect(
                                                                                                    child.path
                                                                                                ),
                                                                                            <DocumentTextIcon
                                                                                                className="h-4 w-4 shrink-0"
                                                                                                aria-hidden="true"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-1.5 text-xs text-primary-600">
                                                                                    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                                                                                        <LinkIcon className="h-3.5 w-3.5 shrink-0" />
                                                                                    </span>
                                                                                    <div className="min-w-0 flex-1">
                                                                                        {renderPageSlugInlineField(
                                                                                            child.path,
                                                                                            () =>
                                                                                                handlePageSelect(
                                                                                                    child.path
                                                                                                )
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="mt-0.5 flex items-center gap-1">
                                                                                {renderStatusBadges(
                                                                                    child.path
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        <div className="ml-2 flex items-center gap-1">
                                                                            <button
                                                                                type="button"
                                                                                title="Edit Page Settings"
                                                                                onClick={() =>
                                                                                    openPageSettingsFromMenu(
                                                                                        child.path
                                                                                    )
                                                                                }
                                                                                className={
                                                                                    INLINE_ACTION_BUTTON_CLASSNAME
                                                                                }
                                                                            >
                                                                                <Cog6ToothIconOutline className="h-4 w-4" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                title="Duplicate Page"
                                                                                onClick={() =>
                                                                                    openDuplicatePageInlineEditor(
                                                                                        child.path
                                                                                    )
                                                                                }
                                                                                className={
                                                                                    INLINE_ACTION_BUTTON_CLASSNAME
                                                                                }
                                                                                disabled={
                                                                                    !!duplicatingPage
                                                                                }
                                                                            >
                                                                                <DocumentDuplicateIcon className="h-4 w-4" />
                                                                            </button>
                                                                            {child.path !==
                                                                                'index' && (
                                                                                <button
                                                                                    type="button"
                                                                                    title="Delete Page"
                                                                                    onClick={() =>
                                                                                        openDeleteTargetPageFromMenu(
                                                                                            child.path
                                                                                        )
                                                                                    }
                                                                                    className={
                                                                                        INLINE_DANGER_ACTION_BUTTON_CLASSNAME
                                                                                    }
                                                                                >
                                                                                    <TrashIcon className="h-4 w-4" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {renderDuplicateInlineForm(
                                                                        child.path
                                                                    )}
                                                                    {renderDeleteInlineForm(
                                                                        child.path
                                                                    )}
                                                                </div>
                                                            </Fragment>
                                                        );
                                                    })}
                                                    {renderTrailingDropZone(
                                                        `folder:${folder}:end`,
                                                        {
                                                            targetFolder:
                                                                folder,
                                                        }
                                                    )}
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    )}

                    {pageListError ? (
                        <p className="text-xs text-red-500">{pageListError}</p>
                    ) : null}
                </div>
            </EditorDrawer>

            {/* Drawers & Modals */}
            <AddComponent />
            <SiteSettingsEditor />

            <ConfirmationModal
                variant="danger"
                title={
                    deleteTargetType === 'folder'
                        ? 'Delete Folder'
                        : 'Delete Page'
                }
                message={
                    <p className="text-sm text-gray-700">
                        Are you sure you want to delete the
                        <strong>
                            {' '}
                            {deleteTargetType === 'folder'
                                ? formatPageName(deleteTargetPage || pagename)
                                : getPageDisplayName(
                                      deleteTargetPage || pagename
                                  )}
                        </strong>{' '}
                        {deleteTargetType === 'folder' ? 'folder' : 'page'}?
                    </p>
                }
                prompt="Delete"
                open={isDeletePageOpen}
                onClose={closeDeletePageModal}
                loading={editor.page.deleting || isDeletingTargetPage}
                onConfirm={handleConfirmDeletePage}
            />

            {/* Spacer to clear the fixed navbar */}
            <div className="h-12" />
        </>
    );
}
